<template>
  <div id="main-container">
    <template v-if="iframe">
      <div id="iframe">
        <img
          src="images/spiral.svg"
          class="spiral"
          alt="spiral"
          title="Knotilus Spiral"
        />
        <p>
          Someone tried to frame
          <img
            src="images/knotilus-logo.webp"
            class="logo"
            alt="Knotilus Logo"
            title="Logo"
          />. Literally...
        </p>
        <p>But this puzzle doesn't unravel just anywhere.</p>
        <a :href="iframeRedirectURL" target="_blank">{{ iframeLinkText }}</a>
      </div>
    </template>
    <template v-else>
      <header>
        <section class="knotilus-logo">
          <img
            src="images/knotilus-logo.webp"
            title="Knotilus Daily Puzzle Game"
            alt="Knotilus Logo"
            @click="tripleClick"
          />
          <knot-select
            :items="levels"
            :defaultItem="currentLevel"
            disabled
            :itemHeight="3.8"
            :itemWidth="3.8"
            :activeSelect="activeSelect"
            id="header"
          ></knot-select>
        </section>
        <div class="header-icons">
          <img
            src="images/settings.svg"
            title="Settings"
            @click="setModal('Settings')"
            alt="Game Settings"
          />
        </div>
        <div class="header-icons">
          <img
            src="images/stats.svg"
            title="Stats"
            @click="setModal('Stats')"
            alt="Player Statistics"
          />
        </div>
        <div class="header-icons">
          <img
            src="images/privacy.svg"
            title="Privacy Notice"
            @click="setModal('Help', 'privacy')"
            alt="Privacy Notice"
          />
        </div>
        <div class="header-icons">
          <img
            src="images/help.svg"
            title="Help"
            @click="setModal('Help')"
            alt="Help: How to Play"
          />
        </div>
        <div class="header-icons">
          <img
            src="images/hamburger.svg"
            title="More"
            @click="setModal('Hub')"
            alt="More: All Options"
          />
        </div>
      </header>
      <section id="info-banner">
        <div id="hint-timer-container">
          <div id="hint-container" v-if="puzzleLoaded" @click="getHint">
            <label>Hints</label>
            <img
              v-for="index in requiredBoardSelection"
              :key="index"
              :src="hintImage(index)"
            />
          </div>
          <div v-else></div>
          <div id="timer-container">
            <knot-timer
              :referenceTime="status.startTimestamp"
              :elapsedTimeFormater="formatElapsedTime"
              :completeTime="status.endTimestamp - status.startTimestamp"
              @setElapsed="unloadPuzzle"
              :saveProgress="settings.saveProgress"
              v-if="puzzleLoaded & settings.showTimer"
              ref="gameTimer"
              size="2vh"
            ></knot-timer>
          </div>
        </div>
        <transition-group name="toasts" tag="div" id="toast-container">
          <div
            class="toast"
            v-for="toast in toasts.slice().reverse()"
            :key="toast.id"
            aria-live="assertive"
            role="alert"
            @click="handleToastClick($event, toast.id)"
            v-html="toast.message"
          ></div>
        </transition-group>
      </section>
      <template v-if="!puzzleLoaded">
        <section id="start">
          <template v-if="!puzzleLoading">
            <section class="knotilus-logo">
              <img
                src="images/knotilus-logo.webp"
                title="Knotilus Daily Puzzle Game"
                alt="Knotilus"
              />
              <knot-select
                :items="levels"
                @select-item="levelSelected"
                :defaultItem="currentLevel"
                :disabled="puzzleLocked"
                :itemHeight="5.1"
                :itemWidth="5.1"
                id="level-select"
                :activeSelect="activeSelect"
              ></knot-select>
            </section>
            <div v-if="!puzzleCompleted">
              Find three Fibonacci-like sequences that have only
              <span class="knot-color">one</span> number in common, the
              <span class="knot-color">knot</span>, all other numbers being
              different
            </div>
            <template v-else>
              <div>Great work on puzzle No. {{ puzzleNumber }}!</div>
              <div>
                You found {{ numberToString(foundSequences.length) }} of the
                {{
                  numberToString(status.puzzleInfo.possible_sequences.length)
                }}
                sequences in {{ formatElapsedTime(puzzleTime) }} using
                {{ revealedHintsStr }}
              </div>
            </template>
            <knot-select
              :items="allModes"
              :defaultItem="currentMode"
              :disabled="puzzleLocked"
              :itemWidth="10.2"
              @select-item="modeSelected"
              id="mode-select"
              :activeSelect="activeSelect"
            ></knot-select>
            <div class="large" v-if="!puzzleCompleted">Ready?</div>
            <button
              @click="startPuzzle"
              :aria-label="startButtonLabel"
              class="single-button"
            >
              {{ startButtonLabel }}
            </button>
            <div class="small">
              {{ displayDate }} <br />
              <template v-if="isArchivePlay">
                <span class="archive-indicator">Archive Puzzle</span><br />
              </template>
              No. {{ puzzleNumber }}
            </div>
            <multi-action-button
              :dynamicActions="statisticsDynamicActions"
              :staticAction="archiveStaticAction"
              dynamic-side="right"
              @set-modal="setModal"
              :slide-interval="5000"
            >
            </multi-action-button>

            <multi-action-button
              :dynamicActions="partnerGamesActions"
              :staticAction="moreStaticAction"
              dynamic-side="left"
              @set-modal="setModal"
              :slide-interval="5000"
              :randomized="true"
              v-if="puzzleCompleted"
              trackEventTag="Partner Games"
            >
            </multi-action-button>
            <template
              v-if="
                GoldenRatioLeagueStarted && playerTeamSet && isCurrentSeason
              "
            >
              <div class="player-team" v-if="settings.livePlaysLeft === null">
                <img :src="settings.team.image_url" class="player-team-image" />
                #Team{{ settings.team.title }}
              </div>
              <div class="small" v-else-if="!isArchivePlay">
                {{ livePlaysLeftAlert }} in the Golden Ratio League!
              </div>
            </template>
          </template>
          <template v-else>
            <div class="spinner">
              <img src="images/spiral.svg" />
              <div class="large">Loading...</div>
            </div>
          </template>
        </section>
      </template>
      <template v-else>
        <knot-board
          :status="status"
          ref="board"
          :mode="status.mode"
          @row-tapped="updateBoardSelection"
          :intersection-set="intersectionSet"
          :error-array="errorArray"
          :focal-on="focalOn"
          :reverse-fill="settings.reverseFill"
          :sixSevenPossible="sixSevenPossible"
        ></knot-board>
        <div id="fill-direction">
          <div
            @click="quickReverseFillToggle"
            title="Press 'R' to toggle the fill direction."
          >
            Fill Direction
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 54 54"
              :class="{ flipped: settings.reverseFill }"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              xml:space="preserve"
              xmlns:serif="http://www.serif.com/"
              style="
                fill-rule: evenodd;
                clip-rule: evenodd;
                stroke-linejoin: round;
                stroke-miterlimit: 2;
                fill: var(--border-color);
              "
            >
              <path
                d="M42.348,28.875l-40.473,0c-1.035,0 -1.875,-0.84 -1.875,-1.875c-0,-1.035 0.84,-1.875 1.875,-1.875l40.473,0l-8.049,-8.049c-0.732,-0.732 -0.732,-1.92 0,-2.652c0.732,-0.732 1.92,-0.732 2.652,0l11.25,11.25c0.352,0.353 0.549,0.827 0.549,1.326c0,0.499 -0.197,0.973 -0.549,1.326l-11.25,11.25c-0.732,0.732 -1.92,0.732 -2.652,-0c-0.732,-0.732 -0.732,-1.92 0,-2.652c0,0 5.173,-5.173 8.049,-8.049Z"
              />
              <path
                d="M54,38.25c0,1.035 -0.84,1.875 -1.875,1.875c-1.035,0 -1.875,-0.84 -1.875,-1.875l0,-22.5c0,-1.035 0.84,-1.875 1.875,-1.875c1.035,0 1.875,0.84 1.875,1.875l0,22.5Z"
              />
            </svg>
          </div>
        </div>
        <transition-group tag="div" name="keyboard-area" id="keyboard-area">
          <sequence-help
            :intersectionSet="intersectionSet"
            :totalRows="status.puzzleInfo.possible_sequences.length"
            :selectedRowCount="selectedRowCount"
            :tooManyClicks="tooManyClicks"
            v-if="sequenceSelectTextVisible"
          ></sequence-help>
          <knot-keyboard
            :can-Submit="canSubmit"
            :keys="keys"
            :reverseFill="settings.reverseFill"
            @key-pushed="onKey"
            :disabled="puzzleCompleted || !puzzleLoaded"
            v-else
          ></knot-keyboard>
        </transition-group>
      </template>

      <div
        v-if="badDimensions || visibleModal"
        class="modal-overlay"
        :class="{ 'bad-dimensions': badDimensions }"
      >
        <div class="modal-container" v-if="badDimensions">
          <p>Your window's current size is a bit off the Fibonacci spiral.</p>
          <p>Resize or rotate to get back to the puzzle.</p>
          <img
            src="images/spiral.svg"
            title="Knotilus"
            alt="Knotilus Spiral"
            id="off-spiral"
          />
        </div>
        <template v-else>
          <template v-if="visibleModal === 'Stats'">
            <statistics-modal
              :saveProgress="settings.saveProgress"
              :resetModal="resetModal"
              :statistics="statistics"
              :status="status"
              :puzzleCompleted="puzzleCompleted"
              :levels="levels"
              :elapsedTimeFormater="formatElapsedTime"
              :puzzleTime="puzzleTime"
              :foundSequencesStr="foundSequencesStr"
              :isArchivePlay="isArchivePlay"
              :displayDate="displayDate"
              :puzzleNumber="puzzleNumber"
              @share="shareAction"
              @set-modal="setModal"
              :default-level="currentLevel"
              :partner-games-actions="partnerGamesActions"
              :more-static-action="moreStaticAction"
              :spotlights="spotlights"
            ></statistics-modal>
          </template>
          <template v-else-if="visibleModal === 'Help'">
            <help-modal
              :level="status.level"
              :resetModal="resetModal"
              :saveProgress="settings.saveProgress"
              @toggled="settingByToggle"
              :selected-slide="slide"
            ></help-modal>
          </template>
          <template v-else-if="visibleModal === 'Settings'">
            <settings-modal
              :resetModal="resetModal"
              :archiveDate="puzzleDate"
              :saveProgress="settings.saveProgress"
              :reverseFill="settings.reverseFill"
              :haptic="settings.haptic"
              :showTimer="settings.showTimer"
              :darkMode="settings.darkMode"
              :archiveHistory="archiveHistory"
              :selected-slide="slide"
              @toggled="settingByToggle"
              @set-puzzle="setPuzzleDate"
              @set-Modal="setModal"
            ></settings-modal>
          </template>
          <template v-else-if="visibleModal === 'Benchmarks'">
            <benchmarks-modal
              :resetModal="resetModal"
              :status="status"
              :puzzleCompleted="puzzleCompleted"
              :elapsedTimeFormater="formatElapsedTime"
              :levels="levels"
              :isArchivePlay="isArchivePlay"
              :displayDate="displayDate"
              :puzzleNumber="puzzleNumber"
              :benchmarkData="benchmarkData"
              :foundSequencesCount="foundSequences.length"
              :revealedHints="revealedHints"
              :totalErrors="totalErrors"
              :puzzleTime="puzzleTime"
            ></benchmarks-modal>
          </template>
          <template v-else-if="visibleModal === 'Announcements'">
            <announcement-modal
              :resetModal="resetModal"
              :announcements="activeAnnouncements"
              @dismiss="dismissAnnouncement"
            ></announcement-modal>
          </template>
          <template v-else-if="visibleModal === 'PartnerGames'">
            <partner-games-modal
              :resetModal="resetModal"
              :partnerGames="partnerGames"
            ></partner-games-modal>
          </template>
          <template v-else-if="visibleModal === 'Hub'">
            <hub-modal
              :resetModal="resetModal"
              :items="hubData"
              @set-Modal="setModal"
              :puzzleCompleted="puzzleCompleted"
            ></hub-modal>
          </template>
          <template v-else-if="visibleModal === 'Leaderboard'">
            <leaderboard-modal
              :resetModal="resetModal"
              :leaderboardData="leaderboardData"
              :elapsedTimeFormater="formatElapsedTime"
              :team="settings.team"
              :teamsData="teamsData"
              :priorSeasonTeam="settings.priorSeasonTeam"
              :livePlaysLeftAlert="livePlaysLeftAlert"
            ></leaderboard-modal>
          </template>
        </template>
      </div>
    </template>
  </div>
  <footer>
    <div id="general-footer">
      <div class="small">{{ footerText }}</div>
      <div>&copy; {{ currentYear }} Knotilus</div>
      <a href="https://ko-fi.com/KnotilusPuzzle" target="_blank"
        >Support Knotilus</a
      >
    </div>
    <div id="social-media">
      <div id="hashtag">#KnotilusPuzzle</div>
      <div id="social-media-icons">
        <a
          v-for="site in socialMedia"
          :key="site.name"
          :href="site.href"
          class="masked-icon"
          :style="{ mask: `url(${site.imgPath})` }"
          :alt="site.alt"
          :title="site.title"
        >
        </a>
      </div>
    </div>
  </footer>
</template>

<script>
const intersectionSetFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

import {
  NUM_TO_STR,
  STATUS,
  MODE,
  SUBMISSION_IMAGES,
  KNOTILUS_EMOJIS,
  GOLDEN_RATIO_LEAGUE,
  SVGS,
  FILL_DIRECTION,
} from "./types.js";
import axios from "axios";
import { ref } from "vue";
import Dexie from "dexie";

const DBNAME = "KnotilusDB";
let knotilusDB = null;

axios.defaults.withCredentials = false;
const GAMES_BINS = [
  { label: "0", max: 0 },
  { label: "1", max: 1 },
  { label: "2-4", max: 4 },
  { label: "5-9", max: 9 },
  { label: "10-24", max: 24 },
  { label: "25-49", max: 49 },
  { label: "50-99", max: 99 },
];

const GAMES_COMPLETED_MAX = "100+";

function fallbackCopyToClipboard(text) {
  // Create a temporary textarea element
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);

  // Select the text inside the textarea
  textarea.select();
  textarea.setSelectionRange(0, 99999); // For mobile devices

  // Copy the text to the clipboard
  try {
    document.execCommand("copy");
    console.log("Text copied to clipboard successfully!");
  } catch (err) {
    console.error("Failed to copy text to clipboard: ", err);
  }

  // Remove the temporary textarea element
  document.body.removeChild(textarea);
}

const timer = (ms) => new Promise((res) => setTimeout(res, ms));
function capitalizeFirstLetter(val) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}
const isLoaded = ref(false);
const firstTime = ref(true);
const clickCount = ref(0);
let clickTimer = null;

export default {
  name: "App",

  data() {
    return {
      sequenceLength: 4,
      defaultBoardRows: 6,
      boardSpacing: 5,
      keySpacing: 3,
      windowHeight: 0,
      windowWidth: 0,
      windowRatio: 1,
      appWidth: 0,
      maxDimension: 0,
      minDimension: 0,
      boardHeight: 0,
      levels: [10, 11, 12],
      announcements: [],
      requiredBoardSelection: 3,
      focalOn: false,
      errorArray: [],
      toasts: [],
      toastId: 0,
      toastTime: 4000,
      saveProgress: false,
      puzzleLoaded: false,
      hapticTime: 5,
      lastModalReset: 0,
      puzzleDate: new Date(new Date().setHours(0, 0, 0, 0)),
      slide: null,
      visibleModal: null,
      redirectSlide: null,
      redirectModal: null,
      statistics: {},
      archiveHistory: new Object(),
      settings: {},
      tooManyClicks: false,
      status: {
        boardData: [],
        puzzleInfo: null,
        level: 10,
        startTimestamp: null,
        endTimestamp: null,
        elapsed: 0,
        selectedRows: [],
        puzzleStatus: STATUS["NOT_STARTED"],
        submissions: [],
        hints: null,
        mode: MODE["EASY"],
        arithmeticErrors: {},
        errorCount: {
          "Not Increasing": 0,
          "Already Submitted": 0,
          "Not Fibonacci-like": 0,
          "Not Enough Numbers": 0,
          "Not Enough Sequences": 0,
          "Unselect Hint": 0,
          "Select Unsubmitted": 0,
          "Select Too Many": 0,
          "Enter Too Many": 0,
          "No Knot": 0,
          "Too Many Knots": 0,
          "Did Not Submit Solution": 0,
        },
      },

      databaseInitialized: false,
      benchmarkData: new Object(),
      intersectionSet: new Set(),
      intersectionSequencesIndices: new Set(),
      partnerGames: [],
      iframe: false,
      activeABTests: {},
      onboardingSelectedSequence: false,
      footerText: "",
      socialMedia: [
        {
          imgPath: "images/social_youtube.svg",
          href: "https://www.youtube.com/@KnotilusPuzzle",
          name: "YouTube",
          alt: "YouTube Logo",
          title: "Follow all things Knotilus on YouTube",
        },
        {
          imgPath: "images/social_x.svg",
          href: "https://www.x.com/knotiluspuzzle/",
          name: "X",
          alt: "X Logo",
          title: "Follow all things Knotilus on X",
        },
        {
          imgPath: "images/social_instagram.svg",
          href: "https://www.instagram.com/knotiluspuzzle/",
          name: "Instagram",
          alt: "Instagram Logo",
          title: "Follow all things Knotilus on Instagram",
        },
        {
          imgPath: "images/social_bluesky.svg",
          href: "https://bsky.app/profile/knotil.us",
          name: "Bluesky",
          alt: "Bluesky Logo",
          title: "Follow all things Knotilus on Bluesky",
        },
      ],
      spotlights: [],
      playerUUID: null,
      dismissedAnnouncements: new Set(),
      leaderboardData: new Object(),
      teamsData: new Object(),
      activeSelect: "",
      archiveStaticAction: {
        title: "Archive",
        modal: ["Settings", "Archive"],
        url: null,
        style: {
          backgroundColor: "var(--border-color)",
          color: "var(--background-color)",
        },
        image: SVGS.ARCHIVE,
      },

      lastRetrieval: new Object(),
      hubData: {
        Insights: [
          {
            name: "Stats",
            modal: ["Stats"],
            svg: SVGS.STATS,
          },
          {
            name: "Benchmarks",
            modal: ["Benchmarks"],
            svg: SVGS.BENCHMARKS,
          },
          {
            name: "Leaderboard",
            modal: ["Leaderboard"],
            svg: SVGS.LEADERBOARD,
          },
        ],
        Information: [
          {
            name: "How To Play",
            modal: ["Help"],
            svg: SVGS.HELP,
          },
          {
            name: "Privacy",
            modal: ["Help", "privacy"],
            svg: SVGS.PRIVACY,
          },
          {
            name: "Partner Games",
            modal: ["PartnerGames"],
            svg: SVGS.HEART,
          },
          {
            name: "Archive",
            modal: ["Settings", "Archive"],
            svg: SVGS.ARCHIVE,
          },
        ],
      },
    };
  },
  async created() {
    if (window.self !== window.top) {
      this.iframe = true;
    }
    let footers = [
      "Fibonacci-Friendly",
      "Puzzling Since 2024",
      "Don't Skip Brain Day",
      "Built To Sequence",
      "Sequence Responsibly",
      "Golden-Ratio Tested",
      "Sequences Ahead",
      "Solve. Share. Repeat.",
    ];
    let now = new Date();
    let daysSinceEEpoch = Math.floor(now.getTime() / (1000 * 60 * 8));
    this.footerText = footers[daysSinceEEpoch % footers.length];
  },
  async mounted() {
    await this.checkIfSavingProgress();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    document.documentElement.style.setProperty(
      "--board-spacing",
      this.boardSpacing + "px"
    );
    document.documentElement.style.setProperty(
      "--key-spacing",
      this.keySpacing + "px"
    );
    window.addEventListener("resize", this.updateDimensions);
    window.addEventListener("keydown", this.desktopKeyDown);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("beforeunload", () => {
      if (this.puzzleLoaded) {
        this.saveToDatabase(true);
      }
    });
    window.addEventListener("storage", this.localStorageSyncing);

    if (
      this.settings.lastCompletedTS === null &&
      this.status.puzzleInfo !== null
    ) {
      this.settings.lastCompletedTS = this.status.endTimestamp;
      this.settings.mode = this.status.mode;
      this.settings.level = this.status.level;
    }

    // Backwards compatibility for settings

    Object.entries(this.getDefaultSettings()).forEach(([key, value]) => {
      if (this.settings[key] == null) {
        this.settings[key] = value;
      }
    });
    // Backwards compatibility for status
    Object.entries(this.getDefaultStatus()).forEach(([key, value]) => {
      if (this.status[key] == null) {
        this.status[key] = value;
      }
    });

    if (
      this.settings.saveProgress &&
      Object.keys(this.settings?.priorSeasonTeam ?? {}).length === 0
    ) {
      await this.retrievePriorSeasonTeam();
    }

    this.updateDimensions();
    this.clearBenchmarkData();
    if (!this.iframe) {
      if (
        this.puzzleDate < today && // An earlier puzzle
        this.archiveHistory[this.formattedDate(today)]?.isComplete !== true && // Today's puzzle is not complete
        this.status.puzzleStatus === STATUS["COMPLETE"]
      ) {
        await this.setPuzzleDate(today);
      }
      if (this.status.puzzleStatus === STATUS["COMPLETE"]) {
        await this.setModal("Stats");
      }
      if (firstTime.value) {
        await this.setModal("Help");
      }
      this.announcements = await this.retrieveAnnouncements();
      if (this.activeAnnouncements.length > 0) {
        await this.setModal("Announcements");
      }
    }
    isLoaded.value = true;
    this.setupABTests();
    this.resetFocalAndErrant();
    this.identifySettings();
    await this.fixPlaythroughs();
  },
  beforeUnmount() {
    this.saveToDatabase();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    window.removeEventListener("resize", this.updateDimensions);
    window.removeEventListener("keydown", this.desktopKeyDown);
    window.removeEventListener("beforeunload", this.saveToDatabase);
    window.removeEventListener("storage", this.localStorageSyncing);
  },
  computed: {
    livePlaysLeftAlert() {
      if (
        this.settings.livePlaysLeft === 0 ||
        this.settings.livePlaysLeft === null
      ) {
        return null;
      }
      if (this.settings.livePlaysLeft === 1) {
        if (
          this.status.puzzleStatus !== STATUS["COMPLETE"] &&
          !this.isArchivePlay
        ) {
          return `Play today's puzzle to get assigned a team`;
        }
        return `Only 1 more live puzzle to get assigned a team`;
      }
      return `Play ${this.settings.livePlaysLeft} more live puzzles to get assigned a team`;
    },
    moreStaticAction() {
      return {
        title: this.partnerGames.length === 0 ? "Games We Love" : "More Games",
        modal: ["PartnerGames"],
        url: null,
        style: {
          backgroundColor: "var(--red-herring-color)",
          color: "var(--background-color)",
        },
        image: SVGS.HEART,
      };
    },
    partnerGamesActions() {
      return this.partnerGames.reduce((acc, item) => {
        acc.push({
          id: item.id,
          title: item.name,
          modal: null,
          url: item.url,
          style: {
            backgroundColor: item.color,
            border: "1px solid rgba(0, 0, 0, 0.185)",
          },
          image: item.image_url,
        });
        return acc;
      }, []);
    },
    statisticsDynamicActions() {
      let actions = [
        {
          id: 1,
          title: "Leaderboard",
          modal: ["Leaderboard"],
          url: null,
          style: {
            backgroundColor: "var(--border-color)",
            color: "var(--background-color)",
          },
          image: SVGS.LEADERBOARD,
        },
      ];
      if (this.puzzleCompleted) {
        actions.push({
          id: 2,
          title: "Benchmarks",
          modal: ["Benchmarks"],
          url: null,
          style: {
            backgroundColor: "var(--border-color)",
            color: "var(--background-color)",
          },
          image: SVGS.BENCHMARKS,
        });
      }
      return actions;
    },
    isCurrentSeason() {
      if (
        this.settings.season === null ||
        typeof this.settings.season !== "object"
      ) {
        return false;
      }
      return (
        new Date().setHours(0, 0, 0, 0) <=
        Date.parse(this.settings.season.end_date)
      );
    },
    playerTeamEmoji() {
      return GOLDEN_RATIO_LEAGUE[this.settings.team?.title.toUpperCase()] || "";
    },
    playerTeamSet() {
      if (
        this.settings.team === null ||
        typeof this.settings.team !== "object"
      ) {
        return false;
      }
      return Object.keys(this.settings.team).length > 0;
    },
    GoldenRatioLeagueStarted() {
      return (
        new Date().setHours(0, 0, 0, 0) >=
        Date.parse(process.env.VUE_APP_SEASON_START_DATE)
      );
    },
    activeAnnouncements() {
      return this.announcements.reduce((acc, item) => {
        if (!this.dismissedAnnouncements.has(item.id)) {
          acc.push(item);
        }
        return acc;
      }, []);
    },
    gamesCompleted() {
      if (!("levels" in this.statistics)) {
        return 0;
      }
      return Object.keys(this.statistics.levels).reduce((acc, lvl) => {
        acc += this.statistics.levels[lvl].gamesCompleted;
        return acc;
      }, 0);
    },
    currentYear() {
      return new Date().getUTCFullYear();
    },
    incompleteABTests() {
      return Object.fromEntries(
        Object.entries(this.settings.abTests).filter(
          ([key]) => this.settings.abTestsStatus[key] !== "Complete"
        )
      );
    },
    iframeLinkText() {
      return `Come untangle the puzzle ${KNOTILUS_EMOJIS.ARROW}`;
    },
    iframeRedirectURL() {
      return `${process.env.VUE_APP_BASE_URL}?utm_source=${document.referrer}`;
    },
    puzzleLoading() {
      return (
        this.status.puzzleStatus === STATUS["LOADING"] ||
        isLoaded.value === false
      );
    },
    allSequencesEntered() {
      return (
        this.enteredSequences.length ===
        this.status.puzzleInfo.possible_sequences.length
      );
    },
    allModes() {
      return Object.values(MODE);
    },
    currentLevel() {
      return this.status.puzzleInfo !== null &&
        this.status.puzzleInfo.date === this.formattedPuzzleDate
        ? this.status.level
        : this.settings.level;
    },
    currentMode() {
      return this.status.puzzleInfo !== null &&
        this.status.puzzleInfo.date === this.formattedPuzzleDate
        ? this.status.mode
        : this.settings.mode;
    },
    puzzleFromArchive() {
      if (this.archiveHistory === null) {
        return null;
      }
      if (this.formattedPuzzleDate in this.archiveHistory) {
        return this.archiveHistory[this.formattedPuzzleDate];
      }
      return null;
    },
    startButtonLabel() {
      if (
        this.status.puzzleInfo !== null &&
        this.status.puzzleInfo.date === this.formattedPuzzleDate
      ) {
        if (this.status.puzzleStatus === STATUS["COMPLETE"])
          return "View Completed Puzzle";
        // if (this.status.level != this.settings.level) {
        //   return "Refresh Page";
        // }
        return "Resume";
      }
      return "Play";
    },
    formattedPuzzleDate() {
      return this.formattedDate(new Date(this.puzzleDate));
    },
    isArchivePlay() {
      if (this.puzzleDate === null) {
        return false;
      }
      return this.puzzleDate.getTime() !== new Date().setHours(0, 0, 0, 0);
    },
    puzzleNumber() {
      return Math.floor(
        (this.puzzleDate - Date.parse(process.env.VUE_APP_START_DATE)) /
          1000 /
          60 /
          60 /
          24
      );
    },
    isHardMode() {
      return this.status.mode === MODE["HARD"];
    },
    hardModeShareStr() {
      return this.isHardMode ? "*" : "";
    },
    intersectionSetStr() {
      if (this.intersectionSet === false) {
        return "";
      }
      return intersectionSetFormatter.format(
        [...this.intersectionSet].sort((a, b) => a - b).map(String)
      );
    },
    selectedSequences() {
      if (this.status.boardData === null) {
        return [];
      }
      return this.status.boardData
        .filter((item) => item.isSelected === true)
        .map((item) => item.sequence);
    },
    selectedSequenceRowIndexes() {
      if (this.status.boardData === null) {
        return [];
      }
      return this.status.boardData.reduce((acc, curr, index) => {
        if (curr.isSelected) {
          acc.push(index);
        }
        return acc;
      }, []);
    },
    selectedRowCount() {
      if (this.status.boardData === null) {
        return 0;
      }
      return this.status.boardData.filter((item) => item.isSelected === true)
        .length;
    },
    nextHintIndex() {
      if (this.foundUnrevealedHints.length > 0) {
        const index = Math.floor(
          Math.random() * this.foundUnrevealedHints.length
        );
        return this.foundUnrevealedHints[index];
      } else {
        return Math.min(...this.hiddenUnrevealedHints);
      }
    },
    totalErrors() {
      if (this.status.errorCount == null) {
        return 0;
      }
      return Object.values(this.status.errorCount).reduce(
        (sum, value) => sum + value,
        0
      );
    },
    revealedHints() {
      if (this.status.hints === null) {
        return 0;
      }
      return this.status.hints.filter((item) => item.revealed === true).length;
    },
    revealedHintsStr() {
      const plural = this.revealedHints === 1 ? "" : "s";
      return `${this.numberToString(this.revealedHints)} hint${plural}`;
    },
    foundUnrevealedHints() {
      return this.status.hints.reduce((acc, curr, index) => {
        if (!this.isNewSequence(curr.sequence) && !curr.revealed) {
          acc.push(index);
        }
        return acc;
      }, []);
    },
    hiddenUnrevealedHints() {
      return this.status.hints.reduce((acc, curr, index) => {
        if (this.isNewSequence(curr.sequence) && !curr.revealed) {
          acc.push(index);
        }
        return acc;
      }, []);
    },
    foundSequences() {
      return this.status.boardData.filter((item) => item.playerFound);
    },

    enteredSequences() {
      return this.status.boardData.filter((item) => item.isLocked);
    },
    foundSequencesStr() {
      if (this.status.puzzleInfo === null) {
        return "";
      }
      return `${this.foundSequences.length}/${this.status.puzzleInfo.possible_sequences.length}`;
    },
    sequenceSelectTextVisible() {
      return (
        !this.isHardMode &&
        this.status.boardData.filter((item) => item.isLocked === true)
          .length === this.status.puzzleInfo.possible_sequences.length
      );
    },
    foundSequencesStrToast() {
      if (this.status.puzzleInfo === null) {
        return "";
      }
      if (
        this.status.boardData.length !==
        this.status.puzzleInfo.possible_sequences.length
      ) {
        return `${capitalizeFirstLetter(
          NUM_TO_STR[this.status.boardData.length]
        )} of ${
          NUM_TO_STR[this.status.puzzleInfo.possible_sequences.length]
        } possible sequences uncovered`;
      } else {
        return `All ${
          NUM_TO_STR[this.status.puzzleInfo.possible_sequences.length]
        } possible sequences uncovered.<br>Now find the three sequences that solve the puzzle!`;
      }
    },
    puzzleCompleted() {
      return this.isPuzzleAtStatus(STATUS["COMPLETE"]);
    },
    puzzleInProgress() {
      return this.isPuzzleAtStatus(STATUS["IN_PROGRESS"]);
    },
    puzzleLocked() {
      return (
        this.puzzleCompleted ||
        this.puzzleInProgress ||
        this.puzzleFromArchive !== null
      );
    },
    badDimensions() {
      return isLoaded.value
        ? this.canvasWidth / this.windowHeight <= 0.34 ||
            this.canvasWidth <= 200
        : false;
    },
    canvasWidth() {
      let ratio = Math.min(this.windowRatio, 0.5);
      return Math.min(this.appWidth, this.windowHeight * ratio) - 10;
    },
    keyWidth() {
      return parseInt(
        (this.canvasWidth - (this.maxKeyInRow - 1) * this.keySpacing) /
          this.maxKeyInRow
      );
    },
    effectiveRowCount() {
      return Math.max(this.defaultBoardRows, this.status.boardData.length);
    },
    boardCellDimension() {
      return Math.min(
        (0.75 * (this.canvasWidth - this.boardSpacing * 3)) / 4,
        (0.88 *
          (this.boardHeight -
            this.boardSpacing * (this.effectiveRowCount - 1))) /
          this.effectiveRowCount
      );
    },
    keys() {
      let currentIndex = 0;
      if (this.status.puzzleInfo === null) return [];
      return this.rowKeyCounts.map((length) => {
        const row = this.status.puzzleInfo.numbers.slice(
          currentIndex,
          currentIndex + length
        );
        currentIndex += length;
        return row;
      });
    },
    rowKeyCounts() {
      if (this.status.level === 12) {
        return [4, 4, 4];
      }
      if (this.status.level === 11) {
        return [4, 3, 4];
      }
      return [3, 4, 3];
    },
    maxKeyInRow() {
      return Math.max(...this.rowKeyCounts);
    },
    currentBoardRowIndex() {
      return this.status.boardData.length - 1;
    },
    currentBoardRow() {
      return this.status.boardData[this.currentBoardRowIndex];
    },
    isRowFilled() {
      if (this.settings.reverseFill) {
        return this.currentBoardCellIndex === -1;
      }

      return this.currentBoardCellIndex === this.sequenceLength;
    },
    isRowEmpty() {
      if (this.settings.reverseFill) {
        return this.currentBoardCellIndex === this.sequenceLength - 1;
      }

      return this.currentBoardCellIndex === 0;
    },
    currentBoardCellIndex() {
      const cellCount = this.currentBoardRow.sequence.filter(
        (item) => item !== null
      ).length;
      if (this.settings.reverseFill) {
        return this.sequenceLength - cellCount - 1;
      }
      return cellCount;
    },
    canSubmit() {
      return this.selectedSequences.length === this.requiredBoardSelection;
    },
    puzzleTime() {
      return Math.floor(
        (this.status.endTimestamp - this.status.startTimestamp) / 1000
      );
    },
    displayDate() {
      return new Date(this.puzzleDate).toLocaleDateString("en-us", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    sixSevenPossible() {
      return (
        this.status?.puzzleInfo?.numbers.filter((value) =>
          [6, 7].includes(value)
        ).length === 2
      );
    },
  },
  watch: {
    announcements: {
      handler: function (newValue) {
        const ids = newValue.reduce((acc, item) => {
          acc.push(item.id);
          return acc;
        }, []);
        const dismissed = [...this.dismissedAnnouncements];
        this.dismissedAnnouncements = new Set(
          dismissed.filter((value) => ids.includes(value))
        );
      },
      deep: true,
      immediate: true,
    },
    dismissedAnnouncements: {
      handler: function (newValue) {
        this.storeLocally(
          "knotilus-dismissed-announcements",
          [...newValue],
          true
        );
      },
      deep: true,
      immediate: false,
    },
    playerUUID(value) {
      if (value !== null) localStorage.setItem("knotilus-player-uuid", value);
    },
    status: {
      handler: function (newValue) {
        this.storeLocally("knotilus-status", newValue);
      },
      deep: true,
      immediate: true,
    },
    settings: {
      handler: function (newValue) {
        this.storeLocally("knotilus-settings", newValue);
      },
      deep: true,
      immediate: true,
    },
    startTimestamp(value) {
      if (value !== null) {
        if (this.status.puzzleInfo !== null) {
          if (
            new Date(`${this.status.puzzleInfo.date}T00:00:00`) >=
            this.puzzleDate
          ) {
            return;
          }
        }
      }
    },
  },
  provide() {
    return {
      trackEvent: this.trackEvent,
    };
  },
  methods: {
    quickReverseFillToggle() {
      if (this.status.puzzleStatus !== STATUS["IN_PROGRESS"]) {
        return;
      }
      this.addToast(
        `Fill Direction changed to ${
          FILL_DIRECTION[!this.settings.reverseFill]
        }`
      );

      this.settingByToggle("reverseFill", !this.settings.reverseFill);
    },
    desktopKeyDown(e) {
      if (e.key === "Escape") {
        this.resetModal();
      }
      if (e.key === "r" || e.key === "R") {
        this.quickReverseFillToggle();
      }
      if (e.key === "Backspace" || e.key === "Delete") {
        if (this.currentBoardRow == null) {
          return;
        }
        this.resetFocalAndErrant();
        this.clearCell();
      }
    },
    tripleClick() {
      clickCount.value++;
      clearTimeout(clickTimer);
      clickTimer = setTimeout(() => {
        clickCount.value = 0;
      }, 800);

      if (clickCount.value === 4) {
        this.setModal("Leaderboard");
      }
    },
    dismissAnnouncement(id) {
      this.dismissedAnnouncements.add(id);
    },
    identifySettings() {
      let settings = ["darkMode", "haptic", "saveProgress"];
      let identify_settings = settings.reduce((acc, label) => {
        acc[label] = this.settings[label];
        return acc;
      }, {});
      this.identify(identify_settings);
      // Games played
      if (this.settings.saveProgress) {
        this.identifyGamesCompleted();
        this.identifyStreakLength();
      }
      // Team and Season
      if (this.GoldenRatioLeagueStarted) {
        this.identify({
          Team: this.settings.team?.title || "",
          Season: this.settings.season?.title || "",
        });
      }
      // Record version
      this.identify({
        version: `v${process.env.VUE_APP_VERSION}`,
      });
    },
    binGames(value) {
      if (this.settings.saveProgress === false) {
        return "Not Saving Progress";
      }
      for (const bin of GAMES_BINS) {
        if (value <= bin.max) {
          return bin.label;
        }
      }
      return GAMES_COMPLETED_MAX;
    },
    identifyGamesCompleted() {
      let identify_settings = {};
      identify_settings["gamesCompleted"] = this.binGames(this.gamesCompleted);
      this.identify(identify_settings);
    },
    identifyStreakLength() {
      let identify_settings = {};
      identify_settings["currentStreak"] = this.binGames(
        this.statistics.currentStreak
      );
      this.identify(identify_settings);
    },
    launchOnboardingToast() {
      let messages = [
        "Tap to build a Fibonacci-like sequence where each number is the sum of the two before",
        // "Start with two numbers and see if the sum is also in the number bank",
        // "Make sure the second number is bigger than the first",
        // "You've got this! We believe in you!",
      ];

      this.onboardingToastActive = true;
      this.addToast(messages.shift());
      this.nextOnboardingToast(messages, 10000);
    },
    nextOnboardingToast(messages, delay) {
      if (messages.length === 0) {
        return;
      }
      setTimeout(() => {
        if (this.onboardingToastActive !== true) {
          return;
        }

        this.addToast(messages.shift());
        this.nextOnboardingToast(messages, delay);
      }, delay);
    },
    reverseFillClear() {
      if (this.currentBoardRow?.sequence == null) {
        return;
      }
      if (!this.isRowEmpty) {
        this.currentBoardRow.sequence = new Array(this.sequenceLength).fill(
          null
        );
        this.addToast(
          "Unsubmitted sequence cleared after fill direction change"
        );
      }
    },
    toggleDarkMode(value) {
      document.documentElement.classList.toggle("dark", value === true);
      document.body.setAttribute("dark-mode-transition", "");
      setTimeout(() => {
        document.body.removeAttribute("dark-mode-transition");
      }, 1000);
    },
    setupABTests() {
      for (const [key, treatments] of Object.entries(this.activeABTests)) {
        if (!(key in this.settings.abTests)) {
          this.settings.abTests[key] =
            treatments[Math.floor(Math.random() * treatments.length)];
        }
        if (!(key in this.settings.abTestsStatus)) {
          this.settings.abTestsStatus[key] = "Running";
        }
      }
    },
    clearBenchmarkData() {
      const keys = ["overall", "matching"];
      keys.forEach((key) => (this.benchmarkData[key] = null));
    },
    setIntersectionSetInfo() {
      const intersectionSet = new Set();
      this.intersectionSequencesIndices = new Set();
      for (let i = 0; i < this.selectedSequences.length; i++) {
        for (let j = i + 1; j < this.selectedSequences.length; j++) {
          const intersection = this.getIntersection(
            this.selectedSequences[i],
            this.selectedSequences[j]
          );
          if (intersection.length !== 0) {
            this.intersectionSequencesIndices.add(i);
            this.intersectionSequencesIndices.add(j);
          }
          intersection.forEach((item) => intersectionSet.add(item));
        }
      }

      this.intersectionSet = intersectionSet;
    },
    handleVisibilityChange() {
      if (document.visibilityState === "hidden") {
        return;
      }
      this.retrieveStatus();
    },
    async retrieveStatus() {
      if (!this.settings.saveProgress) {
        return;
      }
      const storedStatus = await localStorage.getItem("knotilus-status");
      this.status = storedStatus
        ? JSON.parse(storedStatus)
        : this.getDefaultStatus();
      let storedDate;
      if (this.status.puzzleInfo !== null) {
        storedDate = await new Date(`${this.status.puzzleInfo.date}T00:00:00`);
        this.setPuzzleDate(storedDate, false);
      } else {
        this.setPuzzleDate(this.puzzleDate, false);
      }
    },
    numberToString(input) {
      try {
        return NUM_TO_STR[parseInt(input)];
      } catch {
        return input;
      }
    },
    unloadPuzzle(elapsed) {
      this.puzzleLoaded = false;
      this.status.elapsed = elapsed;
      if (this.status.puzzleStatus !== STATUS["COMPLETE"]) {
        this.trackEvent("Puzzle Unloaded");
      }
      this.onboardingToastActive = false;
    },
    async checkIfSavingProgress() {
      const storedSettings = localStorage.getItem("knotilus-settings");
      this.settings = storedSettings
        ? JSON.parse(storedSettings)
        : this.getDefaultSettings();
      firstTime.value = storedSettings === null;
      this.playerUUID = localStorage.getItem("knotilus-player-uuid");
      this.dismissedAnnouncements = new Set(
        JSON.parse(localStorage.getItem("knotilus-dismissed-announcements"))
      );
      if (storedSettings !== null) {
        this.setSaveProgress(this.settings.saveProgress !== null, false);
        if (!("indexedDB" in window) || !("databases" in indexedDB)) {
          this.addToast(
            "Your browser might not support saving progress locally"
          );
          return false;
        }

        if (this.settings.lastCompletedTS === null) {
          this.settings.currentStreak = 0;
        } else {
          const diffDays =
            Math.abs(
              new Date().setHours(0, 0, 0, 0) - this.settings.lastCompletedTS
            ) /
            1000 /
            60 /
            60 /
            24;
          if (diffDays > 1) {
            this.settings.currentStreak = 0;
          }
        }
        try {
          this.databaseInitialized = await this.initializeDatabase(false);
          const results = await knotilusDB.table("status").toArray();
          if (results.length > 0) {
            this.setArchiveHistory(results);
          }
        } catch (error) {
          knotilusDB = null;
          this.databaseInitialized = false;
          console.error("Failed to check database status:", error);
        }
      }

      await this.retrieveStatus();

      await this.retrieveStatistics();
    },
    async clearDatabase() {
      if (!this.databaseInitialized) {
        return;
      }
      this.databaseInitialized = false;
      await knotilusDB.close();
      await knotilusDB.delete();
      knotilusDB = null;
      this.archiveHistory = new Object();
      this.addToast("Local storage cleared");
    },
    async ensurePersistentStorate() {
      if (navigator.storage && navigator.storage.persist) {
        const isPersistent = await navigator.storage.persisted();
        if (!isPersistent) {
          const granted = await navigator.storage.persist();
          console.log(`Persistent storage granted: ${granted}`);
        } else {
          console.log("Storage is already persistent.");
        }
      } else {
        console.warn("Persistent storage API is not supported.");
      }
    },
    async initializeDatabase(emitToast = true) {
      if (knotilusDB !== null) {
        return false;
      }

      knotilusDB = new Dexie(DBNAME);
      knotilusDB.version(1).stores({
        status:
          "id, puzzleDate, isArchivePlay, puzzleStatus, isComplete, elapsed, mode, hintsUsed, level, foundSequences, submissions,startTimestamp, endTimestamp, status",
        statistics: "id",
      });

      await knotilusDB.open().catch((error) => {
        console.error("Failed to open database:", error);
        return false;
      });
      if (emitToast) {
        this.addToast("Local Storage in use");
      }
      await this.ensurePersistentStorate();
      return true;
    },
    async saveToDatabase(updateElapsed = false) {
      if (
        this.status.puzzleInfo === null ||
        !this.databaseInitialized ||
        this.status.boardData.length === 0
      ) {
        return;
      }
      if (this.puzzleFromArchive !== null) {
        if (
          this.puzzleFromArchive.isComplete &&
          this.puzzleFromArchive.stored &&
          this.allSequencesEntered
        ) {
          return;
        }
      }
      if (updateElapsed) {
        this.status.elapsed = Date.now() - this.status.startTimestamp;
      }
      await knotilusDB.table("status").put({
        id: this.status.puzzleInfo.id,
        puzzleDate: this.status.puzzleInfo.date,
        isArchivePlay:
          new Date(`${this.status.puzzleInfo.date}T00:00:00`).getTime() !==
          new Date().setHours(0, 0, 0, 0),
        puzzleStatus: this.status.puzzleStatus,
        isComplete: this.status.puzzleStatus === STATUS["COMPLETE"],
        elapsed: this.status.elapsed,
        mode: this.status.mode,
        hintsUsed: this.revealedHints,
        level: this.status.level,
        foundSequences: this.foundSequences.length,
        submissions: JSON.stringify(this.status.submissions),
        startTimestamp: this.status.startTimestamp,
        endTimestamp: this.status.endTimestamp,
        status: JSON.stringify(this.status),
      });
    },
    isPuzzleAtStatus(status) {
      if (this.status.puzzleInfo === null) {
        return false;
      }
      return (
        this.status.puzzleStatus === status &&
        this.status.puzzleInfo.date === this.formattedPuzzleDate &&
        this.status.puzzleInfo !== null
      );
    },
    storeLocally(label, value, force = false) {
      if (this.settings.saveProgress || force) {
        localStorage.setItem(label, JSON.stringify(value));
      }
    },
    async setPuzzleDate(date, reset = true) {
      this.puzzleLoaded = false;
      if (
        this.status.puzzleStatus === STATUS["IN_PROGRESS"] &&
        this.puzzleFromArchive !== null
      ) {
        this.saveToDatabase();
      }

      if (date !== undefined) {
        if (
          date <= new Date().setHours(0, 0, 0, 0) &&
          date > Date.parse(process.env.VUE_APP_START_DATE)
        ) {
          this.puzzleDate = date;
          if (reset) {
            this.resetModal();
          }
        }
      } else {
        const now = new Date();
        this.puzzleDate = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        );
      }
      await this.getPlaythrough();

      if (this.status.puzzleInfo !== null) {
        if (
          this.status.puzzleStatus === STATUS["COMPLETE"] &&
          this.status.puzzleInfo.date === this.formattedPuzzleDate
        ) {
          if (!this.allSequencesEntered) {
            await this.addNotEnteredSequences(false);
            this.saveToDatabase();
          }
        }
        if (this.status.puzzleInfo.date !== this.formattedPuzzleDate) {
          this.clearBenchmarkData();
          this.resetStatus();
        }
      } else {
        this.resetStatus();
      }
      this.updateDimensions();
    },
    resetBoardSelection() {
      this.status.boardData.forEach((item) => {
        item.isSelected = false;
      });
    },
    hintImage(index) {
      if (index <= this.revealedHints) {
        return "images/lightbulb_filled.svg";
      } else {
        return "images/lock.svg";
      }
    },
    notEnteredSequences() {
      if (!this.puzzleCompleted || this.status.puzzleInfo === null) {
        return [];
      }
      const enteredSet = new Set(
        this.status.boardData.map((row) => JSON.stringify(row.sequence))
      );

      const notEnteredSequences =
        this.status.puzzleInfo.possible_sequences.filter(
          (sequence) => !enteredSet.has(JSON.stringify(sequence))
        );
      return notEnteredSequences;
    },
    settingByToggle(setting, value) {
      this.settings[setting] = value;
      if (setting === "saveProgress") {
        this.setSaveProgress(value);
        this.trackEvent(
          value ? "Saving Progress" : "No Longer Saving Progress"
        );
      } else if (setting === "darkMode") {
        this.toggleDarkMode(value);
      } else if (setting === "reverseFill") {
        this.reverseFillClear();
      }
      // Identify in Umami
      let identify_settings = {};
      identify_settings[setting] = value;
      this.identify(identify_settings);
      return;
    },
    async setSaveProgress(value, afterLoad = true) {
      if (value === undefined) {
        return;
      }
      this.settings.saveProgress = value;
      if (afterLoad) {
        if (value) {
          this.databaseInitialized = await this.initializeDatabase(true);
          this.settings.saveProgress = this.databaseInitialized;
          if (this.status.puzzleStatus !== STATUS["NOT_STARTED"]) {
            await this.updateArchiveHistoryLocally();
            this.saveToDatabase(true);
            if (this.status.puzzleStatus === STATUS["COMPLETE"]) {
              await this.retrieveStatistics(true);
            }
          }
          localStorage.setItem(
            "knotilus-settings",
            JSON.stringify(this.settings)
          );
          if (this.playerUUID !== null) {
            localStorage.setItem("knotilus-player-uuid", this.playerUUID);
          }
        } else {
          localStorage.clear();
          this.clearDatabase();
        }
      }
    },
    formatElapsedTime(elapsed) {
      if (elapsed === null || elapsed === undefined) {
        return "0:00";
      }
      const seconds = elapsed % 60;
      const minutes = Math.floor((elapsed / 60) % 60);
      const hours = Math.floor((elapsed / 3600) % 24);
      const days = Math.floor(elapsed / (3600 * 24));
      let elapsedTime = "";
      if (days > 0) {
        const plural = days === 1 ? "" : "s";
        elapsedTime = `${days} day${plural}`;
      } else if (hours > 0) {
        elapsedTime = `${hours}:${String(minutes).padStart(2, "0")}:${String(
          seconds
        ).padStart(2, "0")}`;
      } else {
        elapsedTime = `${minutes}:${String(seconds).padStart(2, "0")}`;
      }
      return elapsedTime;
    },

    shareText() {
      let text = "";
      if (this.puzzleCompleted && this.status.puzzleInfo !== null) {
        text =
          `Knotilus${this.status.level}${this.hardModeShareStr} No. ${this.puzzleNumber}  \n` +
          this.submissionText() +
          `Time: ${this.formatElapsedTime(this.puzzleTime)} | ` +
          `Errors: ${this.totalErrors} | ` +
          `Hints: ${this.revealedHints}  ` +
          (this.foundSequences.length ===
          this.status.puzzleInfo.possible_sequences.length
            ? `\n${KNOTILUS_EMOJIS.SPIRAL} Completionist - All sequences uncovered!  `
            : "") +
          `\n#KnotilusPuzzle`;

        if (this.GoldenRatioLeagueStarted) {
          text +=
            this.settings.team.title == null
              ? ""
              : ` #Team${this.settings.team.title} ${this.playerTeamEmoji}`;
        }
      } else {
        text = "I like Knotilus!";
      }
      return text;
    },
    submissionText() {
      if (this.status.submissions.length > 6) {
        return `Submissions: ${this.status.submissions.length}  \n`;
      }
      let result = ``;
      const solutionIndices =
        this.status.submissions[this.status.submissions.length - 1];

      this.status.submissions.forEach((submission) => {
        const line = Object.values(submission).reduce((line, value) => {
          return (line +=
            solutionIndices.indexOf(value) >= 0
              ? SUBMISSION_IMAGES["IN"]
              : SUBMISSION_IMAGES["OUT"]);
        }, "");
        result += `${line}  \n`;
      });
      return result;
    },
    getDefaultSettings() {
      return {
        level: 10,
        mode: MODE["EASY"],
        lastCompletedTS: null,
        currentStreak: 0,
        maxStreak: 0,
        saveProgress: false,
        haptic: false,
        showTimer: true,
        abTests: {},
        abTestsStatus: {},
        darkMode: false,
        team: new Object(),
        season: new Object(),
        priorSeasonTeam: new Object(),
        reverseFill: false,
        livePlaysLeft: null,
      };
    },
    getDefaultStatus() {
      return {
        boardData: [],
        puzzleInfo: null,
        level: this.settings.level,
        startTimestamp: null,
        endTimestamp: null,
        elapsed: 0,
        puzzleStatus: STATUS["NOT_STARTED"],
        submissions: [],
        hints: null,
        mode: this.settings.mode,
        arithmeticErrors: {},
        errorCount: {
          "Not Increasing": 0,
          "Already Submitted": 0,
          "Not Fibonacci-like": 0,
          "Not Enough Numbers": 0,
          "Not Enough Sequences": 0,
          "Unselect Hint": 0,
          "Select Unsubmitted": 0,
          "Select Too Many": 0,
          "Enter Too Many": 0,
          "No Knot": 0,
          "Too Many Knots": 0,
          "Did Not Submit Solution": 0,
        },
      };
    },
    async retrieveAnnouncements() {
      const path = `${process.env.VUE_APP_API_BASE_URL}/currentAnnouncements/`;
      try {
        const res = await axios.get(path);
        return res.data.reduce((acc, curr) => {
          if (this.gamesCompleted >= curr.threshold) {
            acc.push(curr);
          }
          return acc;
        }, []);
      } catch (error) {
        console.error("Error retrieving Announcement Data", error);
        return [];
      }
    },
    async retrievePartners() {
      if (this.partnerGames.length !== 0) {
        return this.partnerGames;
      }
      const path = `${process.env.VUE_APP_API_BASE_URL}/partnerGames/`;
      try {
        const res = await axios.get(path);
        return res.data;
      } catch (error) {
        console.error("Error retrieving Partner Games Data", error);
        return [];
      }
    },
    async retrieveSpotlights() {
      if (this.spotlights.length !== 0) {
        return this.spotlights;
      }
      const path = `${process.env.VUE_APP_API_BASE_URL}/currentSpotlights/`;
      try {
        const res = await axios.get(path);
        return res.data;
      } catch (error) {
        console.error("Error retrieving Spotlight Data", error);
        return [];
      }
    },
    localTime(dateStr) {
      const [year, month, day] = dateStr.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    async retrieveOverallBenchmarks() {
      const path = `${process.env.VUE_APP_API_BASE_URL}/overallBenchmarks/${this.formattedPuzzleDate}`;
      axios
        .get(path)
        .then((res) => {
          this.benchmarkData.overall = res.data;
          this.benchmarkData.overall.date = this.localTime(
            this.benchmarkData.overall.date
          );
        })
        .catch((error) => {
          console.error("Error retrieving Overall Benchmark Data:", error);
        });
    },
    async retrieveMatchingBenchmarks() {
      const path = `${process.env.VUE_APP_API_BASE_URL}/matchingBenchmarks/${this.status.level}/${this.status.mode}/${this.formattedPuzzleDate}`;
      axios
        .get(path)
        .then((res) => {
          this.benchmarkData.matching = res.data.metrics;
        })
        .catch((error) => {
          console.error(`Error retrieving Matching Benchmark Data:`, error);
        });
    },
    async retrieveAllLeaderboards() {
      if (!(await this.checkRetrievalNeeded("leaderboards"))) {
        return;
      }
      this.trackEvent("Retrieved Leaderboard", {
        Team: this.settings.team?.title || "",
      });
      const date = this.formattedDate(new Date());
      // await this.retrieveLeaderboard("week", value);
      await this.retrieveWeeklyLeaderboard("c", date);
      await this.retrieveWeeklyLeaderboard("l", date);
      await this.retrieveSeasonMedals("c", date);
      await this.retrieveSeasonMedals("l", date);
      await this.retrieveTeamsData();
      return;
    },
    async retrieveTeamsData() {
      const path = `${process.env.VUE_APP_API_BASE_URL}/teamsData`;
      axios
        .get(path)
        .then((res) => {
          Object.assign(this.teamsData, res.data);
        })
        .catch((error) => {
          console.error(`Error retrieving Teams Data:`, error);
        });
    },
    async retrieveSeasonMedals(indicator, value) {
      if (this.leaderboardData["season"] == undefined) {
        this.leaderboardData["season"] = {};
      }
      const path = `${process.env.VUE_APP_API_BASE_URL}/seasonMedals/${indicator}/${value}`;
      axios
        .get(path)
        .then((res) => {
          Object.assign(this.leaderboardData["season"], res.data);
        })
        .catch((error) => {
          console.error(`Error retrieving Season Medal data:`, error);
        });
    },
    async retrieveWeeklyLeaderboard(indicator, value) {
      if (this.leaderboardData["week"] == undefined) {
        this.leaderboardData["week"] = {};
      }
      const path = `${process.env.VUE_APP_API_BASE_URL}/weeklyLeaderboard/${indicator}/${value}`;
      axios
        .get(path)
        .then((res) => {
          Object.assign(this.leaderboardData["week"], res.data);
        })
        .catch((error) => {
          console.error(`Error retrieving Weekly Leaderboard data:`, error);
        });
    },
    async retrieveLeaderboard(period, value) {
      if (this.leaderboardData[period] == undefined) {
        this.leaderboardData[period] = {};
      }
      const path = `${process.env.VUE_APP_API_BASE_URL}/leaderboards/${period}/${value}`;
      axios
        .get(path)
        .then((res) => {
          this.leaderboardData[period] = res.data;
        })
        .catch((error) => {
          console.error(`Error retrieving ${period} Leaderboard data:`, error);
        });
    },
    async checkRetrievalNeeded(key, minutes = 15, forceRetrieval = false) {
      const now = new Date();
      // Check if last retrieval timestamp was within  the last x minutes
      if (
        (now.getTime() - this.lastRetrieval?.[key]?.getTime()) / 1000 <
          minutes * 60 &&
        !forceRetrieval
      ) {
        return false;
      }
      this.lastRetrieval[key] = now;
      return true;
    },
    async retrieveBenchmarks() {
      if (this.status.puzzleInfo == null) {
        return;
      }
      const newBenchmark =
        this.benchmarkData?.overall?.date?.getTime() !=
        this.localTime(this.status?.puzzleInfo?.date)?.getTime();
      if (!(await this.checkRetrievalNeeded("benchmarks", 15, newBenchmark))) {
        return;
      }

      this.trackEvent("Retrieved Benchmarks", {
        Type: this.isArchivePlay ? "Archive" : "Live",
        Level: this.status.level,
        Mode: this.status.mode,
      });

      await this.retrieveOverallBenchmarks();
      await this.retrieveMatchingBenchmarks();
      return;
    },
    async retrieveStatistics(forceReload) {
      if (!this.databaseInitialized) {
        return;
      }
      try {
        const lastStatistics = await knotilusDB.table("statistics").toArray();
        if (lastStatistics.length > 0) {
          if (
            lastStatistics[0].statisticsTimestamp >=
              this.settings.lastCompletedTS &&
            !forceReload
          ) {
            this.statistics = JSON.parse(lastStatistics[0].data);
            return;
          }
        }
        const gameData = await knotilusDB.table("status").toArray();
        await this.setArchiveHistory(gameData);

        this.statistics = {
          currentStreak: this.settings.currentStreak,
          maxStreak: this.settings.maxStreak,
          levels: gameData.reduce((acc, record) => {
            const playTime = Math.floor(
              (record.endTimestamp - record.startTimestamp) / 1000
            );
            if (record.isComplete) {
              if (!acc[record.level]) {
                acc[record.level] = {
                  gamesCompleted: 0,
                  fastestTime: null,
                  totalTime: 0,
                  hints: 0,
                  submissions: 0,
                  mode: Object.keys(MODE).reduce((accMode, modeStr) => {
                    accMode[MODE[modeStr]] = 0;
                    return accMode;
                  }, {}),
                };
              }
              acc[record.level].gamesCompleted += 1;
              acc[record.level].mode[MODE["EASY"]] +=
                record.mode === MODE["EASY"];
              acc[record.level].mode[MODE["HARD"]] +=
                record.mode === MODE["HARD"];

              acc[record.level].fastestTime =
                acc[record.level].fastestTime === null
                  ? playTime
                  : Math.min(acc[record.level].fastestTime, playTime);
              acc[record.level].totalTime += playTime;
              acc[record.level].hints += record.hintsUsed;
              acc[record.level].submissions += JSON.parse(
                record.submissions
              ).length;
            }
            return acc;
          }, {}),
        };
      } catch {
        this.statistics = this.getDefaultStatistics();
      }
      await knotilusDB.table("statistics").put({
        id: 0,
        statisticsTimestamp: new Date().getTime(),
        data: JSON.stringify(this.statistics),
      });
    },
    getDefaultStatistics() {
      return {
        currentStreak: 0,
        maxStreak: 0,
        levels: {},
      };
    },
    resetModal() {
      document.documentElement.style.setProperty(
        "--button-text-color",
        "var(--background-color)"
      );
      const now = Date.now();
      const cooldown = 500;

      if (now - this.lastModalReset < cooldown) {
        return;
      }
      this.lastModalReset = now;
      this.visibleModal = null;

      if (this.redirectModal !== null) {
        this.setModal(this.redirectModal, this.redirectSlide);
        this.redirectModal = null;
        this.redirectSlide = null;
      }

      this.updateDimensions();
    },
    async setModal(key, slide = null) {
      if (this.visibleModal === "Announcements") {
        this.redirectModal = key;
        this.redirectSlide = slide;
        return;
      } else if (key === "Announcements") {
        this.redirectModal = this.visibleModal;
        this.redirectSlide = null;
      }
      if (
        key !== null &&
        this.puzzleLoaded &&
        this.status.puzzleStatus === STATUS["IN_PROGRESS"]
      ) {
        this.unloadPuzzle(Date.now() - this.status.startTimestamp);
      }
      if (key === "Stats") {
        await this.retrieveStatistics();
        this.partnerGames = await this.retrievePartners();
        this.spotlights = await this.retrieveSpotlights();
      }
      if (key === "Benchmarks") {
        this.retrieveBenchmarks();
      }
      if (key === "Leaderboard") {
        await this.retrieveAllLeaderboards();
      }
      if (key === "PartnerGames") {
        this.partnerGames = await this.retrievePartners();
      }
      this.visibleModal = key;
      this.slide = slide;
      document.documentElement.style.setProperty(
        "--button-text-color",
        "var(--modal-background-color)"
      );
    },
    async getHint() {
      this.onboardingToastActive = false;
      if (this.puzzleCompleted || this.status.puzzleInfo === null) {
        return;
      }

      if (this.nextHintIndex === Infinity) {
        this.addToast("All hints have been revealed");
        return;
      }
      const nextIndex = this.nextHintIndex;
      const sequence = this.status.hints[nextIndex].sequence;
      this.resetBoardSelection();
      if (this.isNewSequence(sequence)) {
        // If the hint sequence has not already been entered
        this.currentBoardRow.sequence = sequence;
        this.lockCurrentRow(false);
        this.addBoardRow();
      }
      this.status.hints[nextIndex].revealed = true;
      this.status.hints.forEach((item) => {
        if (item.revealed) {
          const boardRowIndex = this.getSequenceBoardRowIndex(item.sequence);
          this.status.boardData[boardRowIndex].isSelected = true;
          this.status.boardData[boardRowIndex].isHint = true;
        }
      });
      this.addToast(`Hint ${NUM_TO_STR[this.revealedHints]} revealed`);

      await this.setIntersectionSetInfo();
      if (this.nextHintIndex === Infinity) {
        this.checkGuess();
      }
    },
    handleToastClick(event, toastId) {
      event.currentTarget.classList.add("no-transition");
      this.$nextTick(() => {
        this.removeToast(toastId);
      });
    },
    removeToast(toastId) {
      const toast = this.toasts.find((t) => t.id === toastId);
      if (toast) {
        toast.noTransition = true;
      }
      this.toasts = this.toasts.filter((item) => item.id !== toastId);
      this.checkFocalandError();
    },
    addToast(message, errorArray = [], focalOn = false) {
      this.toastId++;
      if (errorArray.length !== 0) {
        this.errorArray = errorArray;
      } else {
        this.resetErrorArray();
      }
      this.focalOn = focalOn;
      this.toasts.push({
        message: message,
        id: this.toastId,
        errorArray: errorArray,
        focalOn: focalOn,
        noTransition: false,
      });
      setTimeout(
        (toastID) => {
          if (this.toasts.length > 0) {
            // this.toasts.shift();
            // this.checkFocalandError()
            this.removeToast(toastID);
          }
        },
        this.toastTime,
        this.toastId
      );
    },

    checkFocalandError() {
      if (
        this.toasts.filter((item) => item.errorArray.length > 0).length === 0
      ) {
        this.resetErrorArray();
      }
      if (this.toasts.filter((item) => item.focalOn !== false).length === 0) {
        this.focalOn = false;
      }
    },
    startPuzzle() {
      this.getPuzzle();
      this.$nextTick(() => {
        this.updateDimensions();
        if (firstTime.value) {
          this.launchOnboardingToast();
        }
      });
    },
    levelSelected(level, id, active) {
      if (active) {
        this.activeSelect = id;
      }
      if (level === null) {
        return;
      }
      this.settings.level = level;
      if (this.status.puzzleStatus === STATUS["NOT_STARTED"]) {
        this.status.level = level;
      }
      this.updateDimensions();
    },
    modeSelected(mode, id, active) {
      if (active) {
        this.activeSelect = id;
      }
      if (mode === null) {
        return;
      }
      this.settings.mode = mode;
      this.status.mode = mode;
      this.updateDimensions();
    },
    // resetErrorCount() {
    //   Object.keys(this.errorCount).forEach((v) => (this.errorCount[v] = 0));
    // },
    updateBoardSelection(rowIndex) {
      if (this.puzzleCompleted) {
        return;
      }
      if (!this.status.boardData[rowIndex]) {
        return;
      }
      if (this.status.boardData[rowIndex].isHint) {
        this.addToast("Hint sequences cannot be unselected");
        this.status.errorCount["Unselect Hint"]++;
        return;
      }
      if (!this.status.boardData[rowIndex].isLocked) {
        this.addToast("Incomplete sequences cannot be selected");
        this.status.errorCount["Select Unsubmitted"]++;
        return;
      }
      if (
        this.selectedRowCount === this.requiredBoardSelection &&
        !this.status.boardData[rowIndex].isSelected
      ) {
        if (!this.sequenceSelectTextVisible) {
          this.addToast(
            `You can only select a maximum of ${
              NUM_TO_STR[this.requiredBoardSelection]
            } sequences`
          );
        }
        this.status.errorCount["Select Too Many"]++;
        this.tooManyClicks = true;
        return;
      } else {
        this.tooManyClicks = false;
      }
      this.resetFocalAndErrant();
      this.status.boardData[rowIndex].isSelected =
        !this.status.boardData[rowIndex].isSelected;

      this.setIntersectionSetInfo();
      if (this.selectedSequences.length === 3) {
        this.checkGuess();
      }
    },
    resetFocalAndErrant() {
      this.resetErrorArray();
      this.resetFocal();
    },
    resetErrorArray() {
      this.errorArray = this.blankErrorArray();
    },
    resetFocal() {
      this.focalOn = false;
    },
    blankErrorArray() {
      return Array.from({ length: 4 }, () => ({
        operator: null,
        highlightValue: false,
      }));
    },
    onKey(key) {
      this.onboardingToastActive = false;
      try {
        if (this.settings.haptic) {
          navigator.vibrate(this.hapticTime);
        }
      } catch (error) {
        console.error("Error recording Puzzle:", error);
        this.settingByToggle("haptic", false);
        this.addToast(
          "Haptic Keyboard not supported for your device at this time"
        );
      }

      if (typeof key === "number") {
        this.resetFocalAndErrant();
        let success = this.fillCell(key);
        if (!success) {
          return;
        }
      } else if (key === "Backspace") {
        this.resetFocalAndErrant();
        this.clearCell();
      }

      if (this.isRowFilled) {
        this.completeSequence();
      }
    },
    async checkGuess() {
      if (
        !this.isArrayinNestedArray(
          this.selectedSequenceRowIndexes,
          this.status.submissions
        )
      ) {
        this.status.submissions.push(this.selectedSequenceRowIndexes);
      } else {
        this.addToast("Already Guessed");
      }
      if (
        this.intersectionSequencesIndices.size !== this.selectedSequences.length
      ) {
        this.addToast(`No number appears in all selected sequences`, [], true);
        this.status.errorCount["No Knot"]++;
        return false;
      } else if (this.intersectionSet.size > 1) {
        this.addToast(
          `These numbers appear in multiple selected sequences<br>${this.intersectionSetStr}`,
          [],
          true
        );
        this.status.errorCount["Too Many Knots"]++;
        return false;
      }

      await this.markComplete();
      return true;
    },
    async retrievePriorSeasonTeam() {
      if (!(await this.checkRetrievalNeeded("priorSeasonTeam"))) {
        return;
      }
      const path = `${process.env.VUE_APP_API_BASE_URL}/priorSeasonTeam/`;
      axios
        .get(path, {
          headers: {
            playerUUID: this.playerUUID,
            currentDate: this.formattedDate(new Date()),
          },
        })
        .then((res) => {
          this.settings.priorSeasonTeam = res.data.team;
        })
        .catch((error) => {
          console.error("Error retrieving Overall Benchmark Data:", error);
        });
    },
    async recordPlaythrough() {
      const path = `${process.env.VUE_APP_API_BASE_URL}/recordPlaythrough/`;
      try {
        const res = await axios.post(
          path,
          {
            status: this.status,
            version: `v${process.env.VUE_APP_VERSION}`,
            is_archive_play: this.isArchivePlay,
          },
          {
            headers: {
              playerUUID: this.playerUUID,
              optIn: this.settings.saveProgress === true,
            },
          }
        );
        if (this.playerUUID === null && this.settings.saveProgress) {
          this.playerUUID = res.data.playerUUID;
        }
        if (
          (res.data.season?.id !== this.settings.season?.id ||
            res.data.team?.id !== this.settings.team?.id) &&
          this.GoldenRatioLeagueStarted &&
          !this.isArchivePlay
        ) {
          this.settings.team = res.data.team;
          this.settings.season = res.data.season;
          this.settings.livePlaysLeft = res.data.live_plays_left;
          await this.retrievePriorSeasonTeam();
          if (res.data.live_plays_left == null) {
            this.addToast(
              `Welcome to the ${this.settings.team.title} for\n${this.settings.season.title}`
            );
          } else {
            this.addToast(`${this.livePlaysLeftAlert}!`);
            this.addToast(`Your completion helped Team Galaxy`);
          }
        }
        if (res.data.playRecordId !== null) {
          this.status.playRecordId = res.data.playRecordId;
        }
      } catch (error) {
        console.error("Error recording Puzzle:", error);
      }
    },
    async markComplete() {
      this.onboardingToastActive = false;

      this.status.puzzleStatus = STATUS["COMPLETE"];
      this.trackEvent(
        this.isArchivePlay
          ? "Archive Puzzle Completed"
          : "Live Puzzle Completed",
        { level: this.status.level, mode: this.status.mode }
      );
      this.addToast("You got it!");
      if (this.puzzleFromArchive !== null) {
        this.puzzleFromArchive.isComplete = true;
        this.puzzleFromArchive.stored = false;
      }
      const endTimestamp = new Date().setMilliseconds(0);
      this.status.endTimestamp = endTimestamp;
      if (!this.isArchivePlay) {
        this.settings["currentStreak"]++;
        if (this.settings["currentStreak"] > this.settings["maxStreak"]) {
          this.settings["maxStreak"] = this.settings["currentStreak"];
        }
        this.settings.lastCompletedTS = endTimestamp;
      }
      if (this.settings.saveProgress) {
        await this.saveToDatabase();
        await this.retrieveStatistics(true);
      }
      await this.addNotEnteredSequences();

      await timer(3500);

      this.recordPlaythrough();
      this.setModal("Stats");
      if (this.settings.saveProgress) {
        this.identifyGamesCompleted();
      }
    },
    isArrayinNestedArray(newArray, parentArray) {
      if (this.status.puzzleInfo === null) {
        return false;
      }
      for (let i = 0; i < parentArray.length; i++) {
        if (this.arraysIdentical(newArray, parentArray[i])) {
          return true;
        }
      }
      return false;
    },
    fillCell(key) {
      if (this.isRowFilled) {
        let errorArray = this.blankErrorArray();

        errorArray.forEach((item) => {
          item.highlightValue = true;
        });
        this.addToast("A sequence can only have four numbers", errorArray);
        this.status.errorCount["Enter Too Many"]++;
        return false;
      }
      this.currentBoardRow.sequence[this.currentBoardCellIndex] = parseInt(key);
      return true;
    },
    clearCell() {
      if (this.isRowEmpty) return;
      if (this.settings.reverseFill) {
        this.currentBoardRow.sequence[this.currentBoardCellIndex + 1] = null;
      } else {
        this.currentBoardRow.sequence[this.currentBoardCellIndex - 1] = null;
      }
    },
    getIntersection(arr1, arr2) {
      return arr1.filter((value) => arr2.includes(value));
    },
    addArithmeticError({
      leftOperand,
      operator,
      rightOperand,
      submitted,
      resultsAvailable,
    } = {}) {
      let key = `${leftOperand}|${operator}|${rightOperand}|${submitted}|${resultsAvailable}`;
      this.status.arithmeticErrors[key] = 1;
    },
    completeSequence() {
      const increasingErrorIndex = this.isIncreasingSequenceErrorStart(
        this.currentBoardRow.sequence
      );
      if (increasingErrorIndex === 0) {
        let increasingErrorMessage;
        let errorArray = this.blankErrorArray();

        if (
          this.currentBoardRow.sequence[increasingErrorIndex] ===
          this.currentBoardRow.sequence[increasingErrorIndex + 1]
        ) {
          increasingErrorMessage = "The highlighted numbers are equal";
          errorArray[increasingErrorIndex] = {
            operator: SVGS.EQ_SIGN,
            highlightValue: true,
          };
        } else {
          increasingErrorMessage = `${
            this.currentBoardRow.sequence[increasingErrorIndex]
          } is greater than ${
            this.currentBoardRow.sequence[increasingErrorIndex + 1]
          }`;
          errorArray[increasingErrorIndex] = {
            operator: SVGS.GT_SIGN,
            highlightValue: true,
          };
        }
        errorArray[increasingErrorIndex + 1] = {
          operator: null,
          highlightValue: true,
        };
        this.addToast(
          `Sequence must be increasing<br>${increasingErrorMessage}`,
          errorArray,
          true
        );
        this.status.errorCount["Not Increasing"]++;

        return;
      }
      const fibonacciLikeErrorIndex = this.isFibonacciLikeErrorStart(
        this.currentBoardRow.sequence
      );
      if (fibonacciLikeErrorIndex !== null) {
        let errorArray = this.blankErrorArray();
        const fibonacciToast = `Not a Fibonacci-like sequence<br>${
          this.currentBoardRow.sequence[fibonacciLikeErrorIndex]
        } + ${this.currentBoardRow.sequence[fibonacciLikeErrorIndex + 1]} = ${
          this.currentBoardRow.sequence[fibonacciLikeErrorIndex] +
          this.currentBoardRow.sequence[fibonacciLikeErrorIndex + 1]
        }`;
        errorArray[fibonacciLikeErrorIndex] = {
          operator: SVGS.PLUS_SIGN,
          highlightValue: true,
        };
        errorArray[fibonacciLikeErrorIndex + 1] = {
          operator: SVGS.NE_SIGN,
          highlightValue: true,
        };
        errorArray[fibonacciLikeErrorIndex + 2] = {
          operator: null,
          highlightValue: true,
        };
        this.addToast(fibonacciToast, errorArray, true);

        this.status.errorCount["Not Fibonacci-like"]++;
        let leftOperandOffset = 0;
        let submissionOffset = 2;
        let operator = "+";
        let result =
          this.currentBoardRow.sequence[fibonacciLikeErrorIndex] +
          this.currentBoardRow.sequence[fibonacciLikeErrorIndex + 1];
        if (this.settings.reverseFill) {
          leftOperandOffset = 2;
          submissionOffset = 0;
          operator = "-";
          result =
            this.currentBoardRow.sequence[fibonacciLikeErrorIndex + 2] -
            this.currentBoardRow.sequence[fibonacciLikeErrorIndex + 1];
        }

        this.addArithmeticError({
          leftOperand:
            this.currentBoardRow.sequence[
              fibonacciLikeErrorIndex + leftOperandOffset
            ],
          rightOperand:
            this.currentBoardRow.sequence[fibonacciLikeErrorIndex + 1],
          submitted:
            this.currentBoardRow.sequence[
              fibonacciLikeErrorIndex + submissionOffset
            ],
          operator: operator,
          resultsAvailable: this.status.puzzleInfo.numbers.includes(result)
            ? 1
            : 0,
        });

        return;
      }
      if (!this.isNewSequence(this.currentBoardRow.sequence)) {
        let errorArray = this.blankErrorArray();
        errorArray.forEach((item, index) => {
          errorArray[index].highlightValue = true;
        });
        this.addToast("Sequence already submitted", errorArray);
        this.status.errorCount["Already Submitted"]++;
        return;
      }
      this.lockCurrentRow();
      this.addBoardRow();
    },
    resetStatus() {
      this.status = this.getDefaultStatus();
    },
    getSequenceBoardRowIndex(sequence) {
      for (let i = 0; i < this.currentBoardRowIndex; i++) {
        if (this.arraysIdentical(sequence, this.status.boardData[i].sequence)) {
          return i;
        }
      }
      return -1;
    },
    isNewSequence(sequence) {
      return this.getSequenceBoardRowIndex(sequence) === -1;
    },
    arraysIdentical(seq1, seq2) {
      for (let i = 0; i < seq1.length; i++) {
        if (seq1[i] !== seq2[i]) {
          return false;
        }
      }
      return true;
    },
    lockCurrentRow(playerFound = true, inGame = true) {
      this.status.boardData[this.currentBoardRowIndex].isLocked = true;
      this.status.boardData[this.currentBoardRowIndex].playerFound =
        playerFound;
      this.status.boardData[this.currentBoardRowIndex].inGame = inGame;

      if (!this.isHardMode && this.status.puzzleStatus !== STATUS["COMPLETE"]) {
        this.addToast(this.foundSequencesStrToast);
      }
      if (firstTime.value && this.currentBoardRowIndex === 0) {
        this.addToast("Nice!<br/>That's a Fibonacci-like sequence");
      }
    },
    isIncreasingSequenceErrorStart(sequence) {
      for (let i = 0; i !== sequence.length - 1; i++) {
        if (sequence[i] >= sequence[i + 1]) {
          return i;
        }
      }

      return null;
    },
    isFibonacciLikeErrorStart(sequence) {
      for (let i = 2; i < sequence.length; i++) {
        if (sequence[i] !== sequence[i - 1] + sequence[i - 2]) {
          return i - 2;
        }
      }
      return null;
    },
    formattedDate(date) {
      if (!(date instanceof Date)) {
        date = new Date(date);
      }
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`;
    },

    async setArchiveHistory(loadedResults = null) {
      try {
        const results =
          loadedResults || (await knotilusDB.table("status").toArray());

        this.archiveHistory = results.reduce((acc, record) => {
          const playTime = Math.floor(
            (record.endTimestamp - record.startTimestamp) / 1000
          );
          acc[record.puzzleDate] = {
            level: record.level,
            mode: record.mode,
            isComplete: record.isComplete,
            playTime: playTime,
            elapsed: record.elapsed,
            stored: true,
          };
          return acc;
        }, {});
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    async updateArchiveHistoryLocally() {
      if (this.settings.saveProgress) {
        this.archiveHistory[this.formattedPuzzleDate] = {
          level: this.status.level,
          mode: this.status.mode,
          isComplete: this.status.puzzleStatus === STATUS["COMPLETE"],
          stored: false,
        };
      }
    },
    trackEvent(name, data = {}) {
      if (
        typeof window !== "undefined" &&
        typeof window.umami !== "undefined"
      ) {
        if (Object.keys(data).length === 0) {
          window.umami.track(name);
        } else {
          window.umami.track(name, data);
        }
      }
    },
    identify(data) {
      if (
        typeof window !== "undefined" &&
        typeof window.umami !== "undefined"
      ) {
        window.umami.identify(data);
      }
    },
    async fixPlaythroughs() {
      try {
        const count = await knotilusDB.status
          .filter((row) => row.startTimestamp == null)
          .delete();

        if (count > 0) {
          await this.retrieveStatistics(true);
          this.addToast(
            "Some records with playtime errors were removed to keep your statistics accurate"
          );
          this.trackEvent("Fix Playthroughs", { count: count });
        }

        return count;
      } catch (error) {
        return 0;
      }
    },
    async getPlaythrough() {
      if (!this.databaseInitialized) {
        this.resetStatus();
        return;
      }
      try {
        const results = await knotilusDB
          .table("status")
          .where("puzzleDate")
          .equals(this.formattedPuzzleDate)
          .toArray();

        if (results.length > 0) {
          this.status = JSON.parse(results[0].status);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },
    async getPuzzle() {
      if (this.status.puzzleStatus === STATUS["NOT_STARTED"]) {
        this.status.puzzleStatus = STATUS["LOADING"];
      }

      if (
        this.status.puzzleInfo === null ||
        this.status.puzzleInfo.date !== this.formattedPuzzleDate
      ) {
        const path = `${process.env.VUE_APP_API_BASE_URL}/puzzle/${this.status.level}/${this.formattedPuzzleDate}`;
        this.trackEvent(
          this.isArchivePlay
            ? "Retrieved Archive Puzzle"
            : "Retrieved Live Puzzle",
          { level: this.status.level, mode: this.status.mode }
        );
        axios
          .get(path)
          .then((res) => {
            this.status.puzzleInfo = res.data["puzzle_info"];
            this.status.hints = this.status.puzzleInfo.sequences
              .reduce((acc, curr) => {
                acc.push({ sequence: curr, revealed: false });
                return acc;
              }, [])
              .sort((a, b) => {
                if (a.sequence[0] !== b.sequence[0]) {
                  return a.sequence[0] - b.sequence[0];
                }
                const sumA = a.sequence.reduce((sum, num) => sum + num, 0);
                const sumB = b.sequence.reduce((sum, num) => sum + num, 0);
                return sumA - sumB;
              });
            this.status.startTimestamp = new Date().getTime();
            this.status.endTimestamp = null;
            this.status.puzzleStatus = STATUS["IN_PROGRESS"];
            this.$nextTick(() => {
              this.saveToDatabase();
              if (this.status.boardData.length === 0) {
                this.addBoardRow();
              }
            });
            if (this.settings.saveProgress) {
              this.updateArchiveHistoryLocally();
            }
            this.puzzleLoaded = true;
          })
          .catch((error) => {
            this.status.puzzleStatus = STATUS["NOT_STARTED"];
            console.error("Error retrieving Puzzle:", error);
          });
        return;
      }

      if (this.status.puzzleStatus === STATUS["COMPLETE"]) {
        this.setIntersectionSetInfo();
        this.puzzleLoaded = true;
        return;
      }

      if (this.status.puzzleStatus === STATUS["IN_PROGRESS"]) {
        this.setIntersectionSetInfo();
        this.trackEvent("Puzzle Reloaded");

        this.status.startTimestamp = Date.now() - this.status.elapsed;
        this.puzzleLoaded = true;
        const canEnterInput =
          this.status.boardData.filter((item) => item.isLocked === false)
            .length > 0;
        if (!canEnterInput) {
          this.addBoardRow();
        }
        return;
      }
    },
    binInteger(value, min, max) {
      for (let i = min; i < max; i++) {
        if (value <= i) {
          return `${i}`;
        }
      }
      return `${max}+`;
    },
    binTime(value) {
      let binLabels = [
        { label: "<1m", max: 60 },
        { label: "1-2m", max: 120 },
        { label: "2-3m", max: 180 },
        { label: "3-4m", max: 240 },
        { label: "4-5m", max: 300 },
      ];

      for (const bin of binLabels) {
        if (value <= bin.max) {
          return bin.label;
        }
      }
      return "5m+";
    },
    shareAction() {
      let shareData = {
        gamesCompleted: this.binGames(this.gamesCompleted),
        errorCount: this.binGames(this.totalErrors),  
        completionist:
          this.foundSequences.length ===
          this.status.puzzleInfo.possible_sequences.length,
        hintsUsed: this.revealedHints,
        submissionCount: this.binInteger(this.status.submissions.length, 0, 5),
        timeToComplete: this.binTime(
          (this.status.endTimestamp - this.status.startTimestamp) / 1000
        ),
        team: this.settings.team.title,
      }
      this.trackEvent("Shared", shareData);
      this.share(
        `Knotilus ${this.status.level}${this.hardModeShareStr}`,
        this.shareText() + "\n",
        window.location.origin
      );
    },
    share(title, text, url, successToast) {
      if (navigator.share) {
        navigator
          .share({
            title: title,
            text: `${text}${url}`,
          })
          .then(() => {
            console.log("Successfully shared");
            if (successToast) {
              this.addToast(successToast);
            }
          })
          .catch((error) => {
            console.error("Error sharing:", error);
          });
      } else if (navigator.clipboard) {
        navigator.clipboard
          .writeText(text + url)
          .then(() => {
            if (successToast) {
              this.addToast(successToast);
            }
            this.addToast("Copied to clipboard");
          })
          .catch((error) => {
            console.error("Error copying to clipboard:", error);
          });
      } else {
        fallbackCopyToClipboard(text + url);
        if (successToast) {
          this.addToast(successToast);
        }
        this.addToast("Copied to clipboard");
      }
    },
    async addNotEnteredSequences(delayed = true) {
      const sequences = this.notEnteredSequences();
      for (let i = 0; i < sequences.length; i++) {
        if (delayed) {
          await timer(1000);
        }
        if (!this.currentBoardRow.isLocked) {
          this.currentBoardRow.sequence = sequences[i];
          this.lockCurrentRow(false, false);
        } else {
          this.addBoardRow(sequences[i]);
        }
      }
      return sequences.length > 0;
    },
    addBoardRow(sequence = null) {
      let isLocked = false;
      if (sequence === null) {
        sequence = Array(4).fill(null);
      } else {
        isLocked = true;
      }
      this.status.boardData.push({
        sequence: sequence,
        isLocked: isLocked,
        isSelected: false,
        playerFound: false,
        isHint: false,
        inGame: false,
      });
      if (
        this.currentBoardRowIndex === 3 &&
        this.selectedSequences.length === 0 &&
        firstTime.value &&
        this.onboardingSelectedSequence === false
      ) {
        this.addToast("You can select sequences by tapping them");
      }
      this.updateDimensions();
    },
    updateDimensions() {
      this.windowHeight = Math.floor(
        0.9875 * window.visualViewport?.height || window.innerHeight
      );
      // this.windowHeight = window.outerHeight;
      this.windowWidth = window.innerWidth;
      this.windowRatio = this.windowWidth / this.windowHeight;
      this.$nextTick(() => {
        if (this.$refs.board !== undefined && this.$refs.board !== null) {
          this.boardHeight = this.$refs.board.$el.offsetHeight - 5;
          document.documentElement.style.setProperty(
            "--board-cell-dimension",
            `${this.boardCellDimension}px`
          );
        }
      });
      this.maxDimension = Math.max(window.innerHeight, window.innerWidth);
      this.minDimension = Math.min(window.innerHeight, window.innerWidth);
      const appElement = document.getElementById("main-container");
      const appStyles = window.getComputedStyle(appElement);
      this.appWidth =
        appElement.offsetWidth -
        parseFloat(appStyles.paddingRight) -
        parseFloat(appStyles.paddingLeft);
      document.documentElement.style.setProperty(
        "--canvas-width",
        `${this.canvasWidth}px`
      );
      document.documentElement.style.setProperty(
        "--key-width",
        `${this.keyWidth}px`
      );
      document.documentElement.style.setProperty(
        "--vh",
        `${this.windowHeight}px`
      );
    },
    localStorageSyncing(event) {
      const newValue = JSON.parse(event.newValue);
      if (
        event.key === "knotilus-status" &&
        newValue?.puzzleStatus === STATUS["IN_PROGRESS"] &&
        this.status?.puzzleInfo?.date === newValue?.puzzleInfo?.date
      ) {
        this.trackEvent("Multiple Tabs");
        this.maintainStatus(newValue);
      }
    },
    maintainStatus(newValue) {
      if (this.status.startTimestamp == null) {
        this.status = newValue;
      } else if (newValue.startTimestamp < this.status.startTimestamp) {
        this.status.startTimestamp = newValue.startTimestamp;
      } else if (newValue.startTimestamp > this.status.startTimestamp) {
        this.storeLocally("knotilus-status", this.status);
      }
    },
  },
};
</script>

<style>
@font-face {
  font-family: "Fira Sans";
  src: url("fonts/FiraSans-Bold.ttf") format("ttf"),
    url("fonts/firasans-bold-webfont.woff") format("woff"),
    url("fonts/firasans-bold-webfont.woff2") format("woff2");
  font-style: normal;
  font-weight: 700;
}

@font-face {
  font-family: "Fira Sans";
  src: url("fonts/FiraSans-BoldItalic.ttf") format("ttf"),
    url("fonts/firasans-bolditalic-webfont.woff") format("woff"),
    url("fonts/firasans-bolditalic-webfont.woff2") format("woff2");
  font-style: italic;
  font-weight: 700;
}

@font-face {
  font-family: "Fira Sans";
  src: url("fonts/FiraSans-Regular.ttf") format("ttf"),
    url("fonts/firasans-regular-webfont.woff") format("woff"),
    url("fonts/firasans-regular-webfont.woff2") format("woff2");
  font-style: normal;
  font-weight: 400;
}

@font-face {
  font-family: "Fira Sans";
  src: url("fonts/FiraSans-Italic.ttf") format("ttf"),
    url("fonts/firasans-italic-webfont.woff") format("woff"),
    url("fonts/firasans-italic-webfont.woff2") format("woff2");
  font-style: italic;
  font-weight: 400;
}

@font-face {
  font-family: "Roboto Condensed";
  src: url("fonts/RobotoCondensed-VariableFont_wght.ttf") format("ttf"),
    url("fonts/robotocondensed-variablefont_wght-webfont.woff") format("woff"),
    url("fonts/robotocondensed-variablefont_wght-webfont.woff2") format("woff2");
  font-style: normal;
}

@font-face {
  font-family: "Roboto Condensed";
  src: url("fonts/RobotoCondensed-Italic-VariableFont_wght.ttf") format("ttf"),
    url("fonts/robotocondensed-italic-variablefont_wght-webfont.woff")
      format("woff"),
    url("fonts/robotocondensed-italic-variablefont_wght-webfont.woff2")
      format("woff2");
  font-style: italic;
}

@font-face {
  font-family: "Sanchez";
  src: url("fonts/Sanchez-Regular.ttf") format("ttf"),
    url("fonts/sanchez-regular-webfont.woff") format("woff"),
    url("fonts/sanchez-regular-webfont.woff2") format("woff2");
  font-style: normal;
}

@font-face {
  font-family: "Sanchez";
  src: url("fonts/Sanchez-Italic.ttf") format("ttf"),
    url("fonts/sanchez-italic-webfont.woff") format("woff"),
    url("fonts/sanchez-italic-webfont.woff2") format("woff2");
  font-style: italic;
}

:root {
  --primary-color: #1789a9;
  --button-color: #71b8c7;
  --disabled-button: #8fa4a9;
  --background-color: #fde2d3;
  --text-color: #333;
  --input-cell-color: #c3d4d7;
  --border-color: #11657c;
  --modal-background-color: #fef2eb;
  --selected-color: #8ac685;
  --board-cell-color: #c7c2bc;
  --red-herring-color: #d61d52;
  --red-herring-background-color: #f59ca5;
  --knot-color: #2a5ee2;
  --knot-color-light: #99adf0;
  --in-progress-color: #8bbeca;
  --share-color: #e56440;
  --disabled: #999;
  --button-text-color: var(--background-color);
  --board-cell-dimension-demo: 6vh;
}

.dark {
  --primary-color: #0a0a0a;
  --background-color: #1f1f1f;
  --text-color: #d0d0d0;
  --border-color: #1789a9;
  --input-cell-color: #383838;
  --modal-background-color: #2b2b2b;
}

.dark footer * {
  color: var(--border-color) !important;
}
.dark .masked-icon {
  background-color: var(--border-color);
}
[dark-mode-transition] * {
  transition: color 1s ease-in-out, background-color 1s ease-in-out;
}
* {
  box-sizing: border-box;
  color: var(--text-color);
  user-select: none;
  touch-action: manipulation;
}
body {
  font-family: "Roboto Condensed", system-ui;
  font-size: 2.475vh;
  background-color: var(--primary-color);
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: calc(var(--vh, 100dvh));
  touch-action: pan-up;
}

#app {
  height: calc(var(--vh, 100dvh) - 10px);
  display: grid;
  grid-template-rows: 1fr 7vh;
  row-gap: 5px;
  overflow: hidden;
  touch-action: manipulation;
  min-width: 0;
}

#main-container {
  border-radius: 5px;
  background-color: var(--background-color);
  grid-template-rows: 8% 3.3% 1fr 3.3% 21%;
  align-items: stretch;
  justify-items: stretch;
  row-gap: 5px;
  border: 1px solid var(--border-color);
  padding: 0 10px 10px;
  overflow: hidden;
  display: grid;
  width: calc(100vw - 10px);
}
footer {
  display: flex;
  column-gap: 5px;
  font-size: 1.79vh;
  width: calc(100vw - 20px);
  justify-self: center;
  align-self: stretch;
  justify-content: stretch;
}
footer * {
  color: var(--background-color);
}
#general-footer,
#social-media {
  display: flex;
  flex: 1 1 auto;
  justify-content: space-between;
  flex-direction: column;
}
#hashtag {
  text-align: right;
  font-style: italic;
}
#social-media {
  display: flex;
  flex-direction: column;
}
#social-media-icons {
  display: flex;
  justify-content: flex-end;
  column-gap: 1.75vh;
  align-items: center;
  font-size: 2vh;
  margin: 0;
}

.masked-icon {
  aspect-ratio: 1 / 1;
  flex: 1 1 auto;
  max-width: 3.5vh;
  background-color: var(--background-color);
  mask-position: center !important;
  mask-repeat: no-repeat !important;
}
#social-media-icons div {
  flex: 1 1 auto;
  color: var(--background-color) !important;
}

header {
  text-align: left;
  display: flex;
  justify-content: flex-end;
  margin: 0 -3px;
  padding: 8px 3px;
  column-gap: 8px;
  align-items: center;
  border-bottom: 2px solid var(--border-color);
}

.important-note {
  font-size: 1.25em;
  margin: 0 8.5vmin;
}

.header-icons {
  aspect-ratio: 1 / 1;
  cursor: pointer;
  max-width: 3.6vh;
  flex: 1 0.5 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icons img {
  height: 100%;
}

header .knotilus-logo {
  flex: 1 0 0;
  justify-self: stretch;
}
header .knotilus-logo img {
  height: 2.5vh;
}

.knotilus-logo {
  justify-content: start;
  display: flex;
  column-gap: 5px;
  align-items: center;
}

.knotilus-logo img {
  height: 3.3vh;
}

#iframe {
  grid-row: 1 / 5;
  align-items: center;
  justify-content: center;
  display: flex;
  text-align: center;
  flex-direction: column;
  gap: 1vh;
  font-size: 2.75vmin;
}

#iframe a,
#iframe a:visited {
  background-color: var(--border-color);
  height: 6.25vh;
  border-radius: 3.4vh;
  text-decoration: none;
  color: var(--background-color);
  display: flex;
  padding: 0 2vh;
  align-items: center;
  margin-top: 3vh;
}

#iframe a:hover {
  background-color: var(--button-color);
}

#iframe p {
  padding: 0;
  margin: 0;
}

#iframe p .logo {
  display: inline-block;
  height: 0.8em;
  vertical-align: baseline;
}

#board,
#start,
#info-banner,
#keyboard,
#help-text {
  padding: 0;
  justify-self: center;
  align-self: stretch;
  width: var(--canvas-width);
  display: flex;
}

#info-banner,
#start {
  justify-content: center;
  align-items: center;
  font-weight: bold;
}

#info-banner {
  position: relative;
}

#hint-timer-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  justify-items: stretch;
  justify-content: stretch;
  font-family: "Fira Sans", system-ui;
  margin: 0;
}

#hint-container {
  justify-self: start;
  font-size: 2vh;
  display: flex;
  gap: 2px;
  align-items: center;
}

#hint-container img {
  height: 22px;
  aspect-ratio: 1/1;
}

#timer-container {
  justify-self: end;
}

#start {
  flex-direction: column;
  row-gap: 1.25vh;
  grid-row: 3/6;
}

#start > div,
#start h1 {
  text-align: center;
  margin: 0 12.5%;
}

#start .large {
  font-size: 1.1em;
}

#start em {
  display: inline-block;
}

#start {
  margin: 0 25%;
  font-weight: bold;
}
.small {
  font-size: 0.8em;
}
.knot-color {
  color: var(--knot-color);
}

.knot-background {
  background: var(--knot-color-light) !important;
}

.red-herring-color {
  color: var(--red-herring-color);
}

.red-herring-background {
  background: var(--red-herring-background-color) !important;
}

.single-button {
  width: 25vh;
  aspect-ratio: 4/1;
  text-decoration: none;
}

.single-button .icon {
  height: 2.5vh;
}

#start button,
.modal-container button {
  border-radius: 3.4vh;
}

button,
.single-button,
.button-group {
  font-size: 2vh;
}

button {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  font-family: "Fira Sans", system-ui;
  font-weight: bold;
  padding: 0;
  gap: 10px;
  overflow: hidden;
  cursor: pointer;
  background: var(--border-color);
  color: var(--background-color);
  border: 0px;
}

button:disabled {
  background-color: var(--disabled-button);
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  font-size: 16px;
  touch-action: manipulation;
}

.bad-dimensions {
  font-size: 5vh;
  font-family: "Fira Sans", system-ui;
  font-weight: bold;
}

.modal-container {
  background-color: var(--modal-background-color);
  color: var(--border-color);
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: calc(var(--vh, 100dvh) - 20px);
  display: block;
  border: 1px solid var(--border-color);
  padding: 4.5vh 10px 1.5vh;
  position: relative;
  width: calc(100vw - 20px);
  touch-action: manipulation;
  align-content: center;
}

.modal-content {
  justify-content: stretch;
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  row-gap: 10px;
  align-items: center;
  touch-action: manipulation;
  width: var(--canvas-width);
  margin: 0 auto;
  position: relative;
}

.modal-main {
  display: flex;
  row-gap: 5px;
  flex-direction: column;
  padding: 0;
  text-align: justify;
  justify-content: safe center;
  touch-action: manipulation;
  grid-row: 2/4;
  align-self: stretch;
}

.inline-img,
.toast img {
  display: inline-block;
  height: 1em;
  vertical-align: middle;
}

.modal-main::-webkit-scrollbar-track,
.slide::-webkit-scrollbar-track {
  background-color: var(--background-color);
  border-radius: 8px;
}

.modal-main::-webkit-scrollbar,
.slide::-webkit-scrollbar {
  width: 8px;
}

.modal-main::-webkit-scrollbar-thumb,
.slide::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background-color: var(--primary-color);
  border: 1px solid var(--border-color);
}

.centered-objects,
.centered-text {
  text-align: center !important;
}

.centered-objects {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 1vh;
}

.modal-container h1,
.modal-container h2,
.modal-container h3 {
  font-family: "Sanchez", system-ui;
  color: var(--border-color);
  text-align: left;
}

.modal-container h1 {
  font-size: 1.4em;
}

.modal-container h2 {
  font-size: 1.125em;
}

.modal-container h3 {
  font-size: 0.8em;
}

.modal-container p,
.modal-container h1,
.modal-container h2 {
  margin: 0;
}

.modal-container .close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 16px;
  z-index: 1001;
  cursor: pointer;
}

.bad-dimensions .modal-container {
  padding: 20px;
  align-items: center;
  text-align: center;
  overflow: hidden;
}

.bad-dimensions img {
  height: 33vmin;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.125s ease;
}

.fade-x-enter-active,
.fade-x-leave-active {
  transition: all 0.125s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-x-enter-from {
  opacity: 0;
  transform: translateX(-25%);
}

.fade-x-leave-to {
  opacity: 0;
  transform: translateX(25%);
}

#toast-container {
  position: relative;
  width: auto;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  align-items: center;
  z-index: 1002;
}

.toast {
  font-size: 0.9em;
  background-color: var(--border-color);
  opacity: 0.9;
  color: var(--background-color);
  padding: 10px 20px;
  font-weight: 500;
  border-radius: 1vh;
  margin-bottom: 5px;
  text-align: center;
  z-index: 1002;
  width: calc(var(--canvas-width) * 0.9);
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  cursor: pointer;
}

.toasts-enter-active,
.toasts-leave-active {
  transition: all 1s ease;
}

.keyboard-area-enter-active,
.keyboard-area-leave-active {
  transition: all 1s ease;
}

.toasts-move {
  transition: all 0.75s ease;
}

.toasts-enter-from {
  opacity: 0;
  transform: translateY(50%);
}

.keyboard-area-enter-from,
.keyboard-area-leave-to {
  opacity: 0;
}

.toasts-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.5s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.archive-indicator {
  font-family: "Fira Sans", system-ui;
  color: var(--border-color);
  font-variant: small-caps;
}

.modal-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.modal-options a,
.modal-options a:link,
.modal-options a:visited,
.modal-options a:hover {
  color: var(--text-color);
  text-decoration: underline;
}

.modal-options:not(:last-child) {
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 5px;
}

.modal-option-label {
  font-weight: bold;
  text-align: left;
}

.modal-option-label .description {
  font-weight: normal;
  font-size: 0.875em;
}

.switch {
  position: relative;
  display: inline-block;
  width: 5.9vh;
  height: 3.3vh;
  flex-shrink: 0;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 3.3vh;
}

.slider:before {
  position: absolute;
  content: "";
  height: 2.5vh;
  width: 2.5vh;
  left: 0.4vh;
  bottom: 0.4vh;
  background-color: var(--border-color);
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 50%;
}

.switch input:checked + .slider {
  background-color: var(--button-color);
}

.switch input:disabled + .slider:before {
  background-color: #888;
}

.switch input:focus + .slider {
  box-shadow: 0 0 1px var(--button-color);
}

.switch input:checked + .slider:before {
  -webkit-transform: translateX(2.5vh);
  -ms-transform: translateX(2.5vh);
  transform: translateX(2.5vh);
}

.move-left-fade-enter-from,
.move-left-fade-leave-to,
.move-right-fade-enter-from,
.move-right-fade-leave-to {
  opacity: 0;
}

.move-left-fade-enter-from,
.move-right-fade-leave-to {
  transform: translateX(-5vh);
}

.move-left-fade-leave-to,
.move-right-fade-enter-from {
  transform: translateX(100%);
}

.move-left-fade-leave-active,
.move-right-fade-leave-active,
.move-left-fade-enter-active,
.move-right-fade-enter-active {
  transition: all 0.5s ease;
}

.none-leave-active,
.none-enter-active,
.none-enter-from,
.none-leave-to {
  transition: none !important;
}
.move-left-fade-leave-active,
.move-right-fade-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
}

.spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  flex-direction: column;
  gap: 5vh;
}

.spinner img,
#iframe .spiral {
  width: 100px;
  /* Adjust size as needed */
  height: 100px;
  animation: spin 1.5s linear infinite;
}

#off-spiral {
  animation: off-spiral 3s linear infinite;
  margin-top: 10vh;
}
.share,
.feedback {
  background: var(--share-color);
  align-self: center;
}

.games-we-love {
  background-color: var(--red-herring-color);
  color: var(--button-text-color);
  border: 0;
}

button img,
.button img,
.button-group svg,
.single-button svg,
.single-button img {
  height: 2.5vh;
  width: 2.5vh;
}

.button-group {
  width: 38.75vh;
  display: flex;
  align-self: center;
  justify-content: center;
  gap: 1vh;
  height: 6.25vh;
}
.button-group div {
  font-family: "Fira Sans", system-ui;
  font-weight: bold;
  margin: 0;
}

button svg,
.button-group svg,
.single-button svg {
  fill-rule: evenodd;
  clip-rule: evenodd;
  stroke-linejoin: round;
  stroke-miterlimit: 2;
  aspect-ratio: 1/1;
  fill: none;
  stroke-width: 3.8px;
}

.stats-label {
  font-size: 16px;
  white-space: pre-line;
}

.stats-value {
  font-size: 27px;
  font-weight: bold;
  color: var(--border-color);
  font-family: "Fira Sans", system-ui;
}

@keyframes spin {
  from {
    transform: rotate(360deg);
  }

  to {
    transform: rotate(0deg);
  }
}

@keyframes off-spiral {
  0% {
    transform: rotate(-135deg);
  }
  15%,
  20% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(-135deg);
  }
}
.no-transition {
  transition: none !important;
}
.small-text {
  font-size: 0.75em;
}
.player-team {
  font-size: 3vh !important;
  font-weight: bold;
  color: var(--text-color);
  height: 6.25vh;
  display: flex;
  align-items: center;
  gap: 1vh;
  font-style: italic;
}
.player-team img {
  height: 100%;
}
#quick-links {
  display: flex;
  justify-content: center;
  gap: 1vh;
}
.quick-link-icons {
  aspect-ratio: 1 / 1;
  margin: 0 !important;
}
.quick-link-icons img {
  height: 5vh;
  cursor: pointer;
}
.metric-description-enter-from,
.metric-description-leave-to {
  opacity: 0;
  transform: translateY(-5vh);
}

.metric-description-enter-active,
.metric-description-leave-active {
  transition: all 0.5s ease;
}

.metric-description-leave-active {
  position: absolute;
  left: 0;
  top: 0;
}
#fill-direction {
  display: flex;
  justify-content: right;
  align-items: center;
  width: calc(var(--canvas-width) - 2vh);
  margin: 0 auto;
}
#fill-direction div {
  font-weight: bold;
  font-size: 1.5vh;
  display: flex;
  justify-content: right;
  gap: 0.875vh;
  align-items: center;
  cursor: pointer;
}
#fill-direction svg {
  width: 3.3vh;
  aspect-ratio: 1 / 1;
}
.flipped {
  transform: scale(-1);
  transform-origin: center;
}
#keyboard-area {
  display: flex;
  justify-self: center;
  position: relative;
  width: calc(var(--canvas-width) - 2vh);
}
</style>
